import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { Leaf, Users, ShoppingCart, TrendUp, Plus, ArrowLeft, Trash, Handshake, Check, X, Crown, CurrencyDollar, Megaphone, Info, Warning, Wrench, CheckCircle, PencilSimple, ChartBar, Target, CalendarBlank, Flask, Bug, Virus, Plant, UploadSimple, DownloadSimple, FileText, Image, Palette, Copy, Link, TShirt, Gear, MagnifyingGlass, List, Moon, Sun, SignOut, ArrowsClockwise, Broadcast, Notebook } from '@phosphor-icons/react';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { EngagementCard } from '../components/EngagementCard';
import { CodeHealthCard } from '../components/CodeHealthCard';
import { DataMigrationCard } from '../components/DataMigrationCard';
import { CalibrationCard } from '../components/CalibrationCard';
import { NfcRedirectCard } from '../components/NfcRedirectCard';
import { VersionBadge } from '../components/VersionBadge';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// ── System Health Widget ──
const SystemHealthWidget = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/health/full`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHealth(data);
        setLastCheck(new Date().toLocaleTimeString());
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runCheck(); }, []);

  const statusIcon = (s) => s === 'ok' ? '🟢' : s === 'warning' ? '🟡' : '🔴';

  return (
    <Card className="border border-border/50" data-testid="health-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Wrench weight="bold" size={18} className="text-primary" />
            System Health
            {health && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                health.overall === 'healthy' ? 'bg-emerald-500/15 text-emerald-600' :
                health.overall === 'degraded' ? 'bg-amber-500/15 text-amber-600' :
                'bg-red-500/15 text-red-600'
              }`}>
                {health.overall}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastCheck && <span className="text-[10px] text-muted-foreground">{lastCheck}</span>}
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={runCheck} disabled={loading} data-testid="health-refresh">
              {loading ? '...' : 'Check'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {health ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(health.checks).map(([name, check]) => (
              <div key={name} className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded bg-muted/30">
                <span>{statusIcon(check.status)}</span>
                <span className="text-foreground/80 capitalize">{name.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Loading...</p>
        )}
        <p className="text-[10px] text-muted-foreground mt-2">Auto-checks on startup + every hour. Email alerts on failures.</p>
      </CardContent>
    </Card>
  );
};

// ── Founders Pulse Widget ──
// Tiny mini-feed shown above the Priorities widget. Pulls the full founders
// list from /api/admin/founding-members on mount, filters to "joined in the
// last 7 days," and renders a calm at-a-glance pulse so the admin sees real
// signup velocity without clicking into the Founders tab. Soft-fails silent
// (admin-only endpoint).
const FoundersPulseWidget = () => {
  const [members, setMembers] = React.useState([]);
  const [count, setCount] = React.useState(null);
  const [cap, setCap] = React.useState(10);
  const navigate = useNavigate();

  React.useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/founding-members`, { withCredentials: true })
      .then((r) => {
        setMembers(r.data?.members || []);
        setCount(r.data?.count ?? null);
        setCap(r.data?.cap ?? 10);
      })
      .catch(() => { /* soft fail — keep widget invisible-ish */ });
  }, []);

  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = members
    .filter((m) => {
      const ts = m.founding_member_since && new Date(m.founding_member_since).getTime();
      return ts && ts >= sevenDaysAgo;
    })
    .sort((a, b) => new Date(b.founding_member_since) - new Date(a.founding_member_since))
    .slice(0, 5);

  const fmtAgo = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <Card
      className="border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-amber-500/5"
      data-testid="founders-pulse-widget"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Crown weight="bold" size={18} className="text-emerald-600 dark:text-emerald-400" />
            Founders Pulse
          </CardTitle>
          <button
            onClick={() => navigate('/admin/founders')}
            className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 hover:underline"
            data-testid="founders-pulse-manage"
          >
            Manage →
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-baseline gap-3 mb-3" data-testid="founders-pulse-counter">
          <div className="text-2xl font-black text-zinc-900 dark:text-zinc-50">
            {count === null ? '—' : count}<span className="text-sm font-normal text-muted-foreground"> / {cap}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            founding seats filled
          </div>
        </div>
        {recent.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 px-3 py-3 text-xs text-muted-foreground"
            data-testid="founders-pulse-empty"
          >
            No new founding signups in the last 7 days. Drop the Ecowitt→Rachio
            outreach link to revive the funnel.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="founders-pulse-list">
            {recent.map((m, i) => (
              <li
                key={m.email || i}
                className="flex items-center justify-between text-xs rounded-md border border-emerald-500/20 bg-white/60 dark:bg-zinc-900/60 px-2.5 py-1.5"
                data-testid={`founders-pulse-row-${i}`}
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="font-semibold text-zinc-900 dark:text-zinc-50 truncate">
                    {m.name || m.email}
                  </span>
                  <span className="text-[10px] text-muted-foreground truncate">
                    {m.email}{m.utm_source ? ` · via ${m.utm_source}` : ''}
                  </span>
                </div>
                <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold ml-2 shrink-0">
                  {fmtAgo(m.founding_member_since)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};



// ── Priorities of the Week Widget ──
const PRIORITY_COLORS = {
  high: { bg: 'bg-red-500/10 border-red-500/30', dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  medium: { bg: 'bg-amber-500/10 border-amber-500/30', dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  low: { bg: 'bg-blue-500/10 border-blue-500/30', dot: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400' },
};

const DEFAULT_PRIORITIES = [
  { id: 'p1', text: 'Deploy Tuesday build to production', priority: 'high', done: false },
  { id: 'p2', text: 'Order VistaPrint truck magnets (after deploy)', priority: 'high', done: false },
  { id: 'p3', text: 'Record 3 TikTok lawn diagnosis demos', priority: 'medium', done: false },
  { id: 'p4', text: 'Post QR code in r/lawncare & r/bermudagrass', priority: 'medium', done: false },
  { id: 'p5', text: 'Implement free/paid tier gating on photo diagnosis', priority: 'medium', done: false },
  { id: 'p6', text: 'Regenerate QR code + banner with production URL', priority: 'low', done: false },
];

const PrioritiesWidget = () => {
  const [priorities, setPriorities] = useState(() => {
    const saved = localStorage.getItem('turfpulse_priorities');
    return saved ? JSON.parse(saved) : DEFAULT_PRIORITIES;
  });
  const [newText, setNewText] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [showAdd, setShowAdd] = useState(false);

  const save = (updated) => {
    setPriorities(updated);
    localStorage.setItem('turfpulse_priorities', JSON.stringify(updated));
  };

  const toggle = (id) => {
    save(priorities.map(p => p.id === id ? { ...p, done: !p.done } : p));
  };

  const remove = (id) => {
    save(priorities.filter(p => p.id !== id));
  };

  const add = () => {
    if (!newText.trim()) return;
    const item = { id: `p_${Date.now()}`, text: newText.trim(), priority: newPriority, done: false };
    save([...priorities, item]);
    setNewText('');
    setShowAdd(false);
  };

  const sorted = [...priorities].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    if (a.done !== b.done) return a.done ? 1 : -1;
    return (order[a.priority] || 1) - (order[b.priority] || 1);
  });

  const doneCount = priorities.filter(p => p.done).length;

  return (
    <Card className="border border-border/50" data-testid="priorities-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Target weight="bold" size={18} className="text-primary" />
            Priorities of the Week
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">{doneCount}/{priorities.length}</span>
            <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${priorities.length ? (doneCount/priorities.length)*100 : 0}%` }} />
            </div>
            <button onClick={() => setShowAdd(!showAdd)} className="p-1 rounded hover:bg-secondary/50 transition-colors" data-testid="priority-add-toggle">
              <Plus size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {showAdd && (
          <div className="flex gap-2 mb-3" data-testid="priority-add-form">
            <input
              className="flex-1 text-sm px-2 py-1.5 rounded-md border border-input bg-background"
              placeholder="New priority..."
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              data-testid="priority-add-input"
            />
            <select
              className="text-xs px-2 py-1.5 rounded-md border border-input bg-background"
              value={newPriority}
              onChange={(e) => setNewPriority(e.target.value)}
            >
              <option value="high">High</option>
              <option value="medium">Med</option>
              <option value="low">Low</option>
            </select>
            <Button size="sm" className="h-8" onClick={add} data-testid="priority-add-submit">Add</Button>
          </div>
        )}
        <div className="space-y-1.5">
          {sorted.map(p => {
            const cfg = PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.medium;
            return (
              <div key={p.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all ${p.done ? 'opacity-40 bg-muted/30 border-border/20' : cfg.bg}`} data-testid={`priority-${p.id}`}>
                <button
                  onClick={() => toggle(p.id)}
                  className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${p.done ? 'bg-primary border-primary' : 'border-current ' + cfg.text}`}
                >
                  {p.done && <Check size={12} weight="bold" className="text-primary-foreground" />}
                </button>
                <span className={`flex-1 text-sm ${p.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{p.text}</span>
                <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${cfg.text}`}>{p.priority}</span>
                <button onClick={() => remove(p.id)} className="p-0.5 rounded hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100 hover:opacity-100">
                  <X size={12} className="text-muted-foreground" />
                </button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSortBy, setUserSortBy] = useState('newest');
  const [manualSearch, setManualSearch] = useState('');

  // Roadmap tasks
  const [roadmapTasks] = useState([
    { id: 't1', priority: 'P0', title: 'Operations Manual', description: 'Complete admin runbook with search, legal, glossary', status: 'done' },
    { id: 't2', priority: 'P0', title: 'Landing Page Quiz Funnel', description: '4-question quiz recommending personalized tier', status: 'done' },
    { id: 't3', priority: 'P0', title: 'Admin Roadmap Board', description: 'Task tracker with priorities inside admin panel', status: 'done' },
    { id: 't4', priority: 'P1', title: 'Rachio API Webhook Integration', description: 'Real water usage tracking based on ET data from Rachio smart controllers', status: 'upcoming' },
    { id: 't5', priority: 'P1', title: 'GTLL Lifetime VIP Account', description: 'Georgetown Lawn Leveling gets free Enthusiast Annual forever — need target email', status: 'blocked', blocker: 'Waiting for GTLL contact email' },
    { id: 't6', priority: 'P2', title: 'Backend Refactoring', description: 'Continue splitting server.py monolith (~7400 lines) into modular routes/', status: 'upcoming' },
    { id: 't7', priority: 'P2', title: 'B2B Fleet Tools', description: 'Multi-crew management, route optimization, fleet-level reporting for lawn care businesses', status: 'backlog' },
    { id: 't8', priority: 'P3', title: 'Native Mobile Apps', description: 'iOS and Android wrappers for the TurfPulse web app', status: 'backlog' },
    { id: 't10', priority: 'P3', title: 'AI Spray Forecasting', description: 'Predictive model for optimal spray windows using weather forecasts + GDD trajectory', status: 'backlog' },
    { id: 't11', priority: 'P3', title: 'Copyright Registration', description: 'File copyright registration with U.S. Copyright Office ($65 filing fee). Protects codebase, UI design, and original content.', status: 'upcoming' },
    { id: 't12', priority: 'P4', title: 'Community Forum', description: 'User-to-user lawn care discussions, tips sharing, photo galleries', status: 'backlog' },
  ]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [irrigationIntegrations, setIrrigationIntegrations] = useState([]);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  
  // Application Products state
  const [applicationProducts, setApplicationProducts] = useState({ pgr: [], weed: [], pest: [], disease: [], counts: {} });
  const [activeProductCategory, setActiveProductCategory] = useState('pgr');

  // Business Tracker state
  const [trackerEntries, setTrackerEntries] = useState([]);
  const [trackerFilter, setTrackerFilter] = useState('all');
  const [showTrackerDialog, setShowTrackerDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [trackerForm, setTrackerForm] = useState({
    entry_type: 'expense', date: new Date().toISOString().split('T')[0],
    description: '', category: '', amount: 0, receipt_info: '', notes: ''
  });
  const [categorySuggestions, setCategorySuggestions] = useState([]);

  // Price Audit state
  const [priceAudit, setPriceAudit] = useState(null);
  const [priceAuditLoading, setPriceAuditLoading] = useState(false);
  const [priceAuditOpen, setPriceAuditOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPriceValue, setNewPriceValue] = useState('');
  const [selectedPlannerDate, setSelectedPlannerDate] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('tracker');
  // Live Inner Circle counter rendered as a badge on the "Founders" nav item.
  // One-shot fetch on mount — cheap public endpoint; admin sees real-time slots.
  const [foundersStats, setFoundersStats] = useState({ count: null, cap: 10 });
  const [showAppProductDialog, setShowAppProductDialog] = useState(false);
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerSaving, setPartnerSaving] = useState(false);
  const [newPartner, setNewPartner] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    service_type: '', area_served: '', description: '', website: '',
  });
  const [editingAppProduct, setEditingAppProduct] = useState(null);
  const [appProductForm, setAppProductForm] = useState({
    name: '', active_ingredient: '', manufacturer: '', description: '',
    application_rate_min: '', application_rate_max: '', rate_unit: 'oz',
    target_gdd_interval: '', reapplication_interval_days: '',
    cost_per_unit: '', unit_size: '', affiliate_link: '',
    epa_registration_number: '', product_type: 'Pre-Emergent'
  });

  // CSV Import state
  const [showCsvImportDialog, setShowCsvImportDialog] = useState(false);
  const [csvImportData, setCsvImportData] = useState('');
  const [csvImportCategory, setCsvImportCategory] = useState('pgr');
  const [csvImporting, setCsvImporting] = useState(false);

  // User tier management
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [showUserTierDialog, setShowUserTierDialog] = useState(false);
  const [editingUserTier, setEditingUserTier] = useState(null);
  const [userTierForm, setUserTierForm] = useState({ tier: 'free', expires_at: '', grass_type: 'cool-season', role: 'user' });

  // User profile editing (grass type, role)
  const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);
  const [editingUserProfile, setEditingUserProfile] = useState(null);
  const [userProfileForm, setUserProfileForm] = useState({ grass_type: 'cool-season', role: 'user' });

  // Marketing & Branding state
  const [marketingAssets, setMarketingAssets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [brandAssets, setBrandAssets] = useState([]);
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [assetForm, setAssetForm] = useState({
    name: '', description: '', category: 'graphic', url: '', type: 'image'
  });
  
  // Product form
  const [productName, setProductName] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [affiliateLink, setAffiliateLink] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [productType, setProductType] = useState('PGR');
  const [markText, setMarkText] = useState('');
  
  // Announcement form
  const [annTitle, setAnnTitle] = useState('');
  const [annMessage, setAnnMessage] = useState('');
  const [annType, setAnnType] = useState('info');
  const [annLinkUrl, setAnnLinkUrl] = useState('');
  const [annLinkText, setAnnLinkText] = useState('');
  const [annDismissible, setAnnDismissible] = useState(true);
  const [annActive, setAnnActive] = useState(true);
  const [annScheduledStart, setAnnScheduledStart] = useState('');
  const [annScheduledEnd, setAnnScheduledEnd] = useState('');

  // Video Guides state
  const [videoGuides, setVideoGuides] = useState([]);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '', description: '', youtube_video_id: '', creator: '',
    category: 'weed', seasons: [], is_active: true
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAdminData();
    // Live Inner Circle count — used as a badge on the Founders nav item so
    // admins can see "Founders · 3/10" without leaving the dashboard.
    axios
      .get(`${API_URL}/api/payments/inner-circle/slots`)
      .then((r) => setFoundersStats({ count: r.data?.claimed ?? null, cap: r.data?.cap ?? 10 }))
      .catch(() => { /* public endpoint — soft fail */ });
  }, [user, navigate]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, productsRes, partnersRes, announcementsRes, analyticsRes, irrigationRes, appProductsRes, tiersRes, marketingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/partners`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/announcements`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/analytics/campaigns`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/irrigation`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/application-products`, { withCredentials: true }).catch(() => ({ data: { pgr: [], weed: [], pest: [], disease: [], counts: {} } })),
        axios.get(`${API_URL}/api/admin/subscription-tiers`, { withCredentials: true }).catch(() => ({ data: { tiers: [] } })),
        axios.get(`${API_URL}/api/admin/marketing`, { withCredentials: true }).catch(() => ({ data: { assets: [], campaigns: [], brand: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProducts(productsRes.data);
      setPartners(partnersRes.data);
      setAnnouncements(announcementsRes.data);
      setCampaignAnalytics(analyticsRes.data);
      setIrrigationIntegrations(irrigationRes.data || []);
      setApplicationProducts(appProductsRes.data);
      setSubscriptionTiers(tiersRes.data?.tiers || []);
      setMarketingAssets(marketingRes.data?.assets || []);
      setCampaigns(marketingRes.data?.campaigns || []);
      setBrandAssets(marketingRes.data?.brand || []);
      // Fetch video guides and business tracker separately (non-blocking)
      fetchVideoGuides();
      fetchTrackerEntries();
      fetchLatestAudit();
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    }
  };

  const fetchVideoGuides = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/video-guides`, { withCredentials: true });
      setVideoGuides(res.data?.videos || []);
    } catch (error) {
      console.error('Error fetching video guides:', error);
    }
  };

  const fetchTrackerEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-tracker`, { withCredentials: true });
      setTrackerEntries(res.data?.entries || []);
    } catch (error) {
      console.error('Error fetching tracker:', error);
    }
  };

  const handleSaveTrackerEntry = async () => {
    if (!trackerForm.description || !trackerForm.date) {
      toast.error('Description and date are required');
      return;
    }
    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/api/admin/business-tracker/${editingEntry.id}`, trackerForm, { withCredentials: true });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API_URL}/api/admin/business-tracker`, trackerForm, { withCredentials: true });
        toast.success('Entry added');
      }
      setShowTrackerDialog(false);
      setEditingEntry(null);
      setTrackerForm({ entry_type: 'expense', date: new Date().toISOString().split('T')[0], description: '', category: '', amount: 0, receipt_info: '', notes: '' });
      fetchTrackerEntries();
    } catch {
      toast.error('Failed to save entry');
    }
  };

  const handleDeleteTrackerEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/business-tracker/${id}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchTrackerEntries();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSuggestCategory = async (description) => {
    if (description.length < 3) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-tracker/suggest-category?description=${encodeURIComponent(description)}`, { withCredentials: true });
      const suggestions = res.data?.suggestions || [];
      if (suggestions.length > 0 && !trackerForm.category) {
        setTrackerForm(prev => ({ ...prev, category: suggestions[0] }));
      }
      setCategorySuggestions(suggestions);
    } catch { /* silent */ }
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-tracker/export-csv`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TurfPulse_Business_Tracker_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/business-tracker/export-pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TurfPulse_Business_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch { toast.error('Export failed'); }
  };

  // Price Audit handlers
  const fetchLatestAudit = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/price-audit/latest`, { withCredentials: true });
      setPriceAudit(res.data);
    } catch { /* no audit yet */ }
  };

  const handleRunAudit = async () => {
    setPriceAuditLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/price-audit/run`, {}, { withCredentials: true });
      setPriceAudit(res.data);
      toast.success(`Audit complete: ${res.data.flagged_count} product(s) flagged`);
    } catch { toast.error('Audit failed'); }
    setPriceAuditLoading(false);
  };

  const handleUpdatePrice = async (productId, category) => {
    if (!newPriceValue || isNaN(parseFloat(newPriceValue))) { toast.error('Enter a valid price'); return; }
    try {
      await axios.put(`${API_URL}/api/admin/price-audit/update-price`, {
        product_id: productId, new_price: parseFloat(newPriceValue), category
      }, { withCredentials: true });
      toast.success('Price updated');
      setEditingPrice(null);
      setNewPriceValue('');
      // Re-run audit to refresh
      handleRunAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const resetVideoForm = () => {
    setVideoForm({ title: '', description: '', youtube_video_id: '', creator: '', category: 'weed', seasons: [], is_active: true });
    setEditingVideo(null);
  };

  const handleSaveVideo = async () => {
    if (!videoForm.title || !videoForm.youtube_video_id || !videoForm.creator || !videoForm.category || videoForm.seasons.length === 0) {
      toast.error('Please fill all required fields and select at least one season');
      return;
    }
    try {
      if (editingVideo) {
        await axios.put(`${API_URL}/api/admin/video-guides/${editingVideo.video_id}`, videoForm, { withCredentials: true });
        toast.success('Video updated');
      } else {
        await axios.post(`${API_URL}/api/admin/video-guides`, videoForm, { withCredentials: true });
        toast.success('Video added');
      }
      setShowVideoDialog(false);
      resetVideoForm();
      fetchVideoGuides();
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video guide?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/video-guides/${videoId}`, { withCredentials: true });
      toast.success('Video deleted');
      fetchVideoGuides();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleToggleVideo = async (videoId) => {
    try {
      await axios.put(`${API_URL}/api/admin/video-guides/${videoId}/toggle`, {}, { withCredentials: true });
      fetchVideoGuides();
    } catch (error) {
      toast.error('Failed to toggle video');
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title || '',
      description: video.description || '',
      youtube_video_id: video.youtube_video_id || '',
      creator: video.creator || '',
      category: video.category || 'weed',
      seasons: video.seasons || [],
      is_active: video.is_active !== false
    });
    setShowVideoDialog(true);
  };

  const toggleSeason = (season) => {
    setVideoForm(prev => ({
      ...prev,
      seasons: prev.seasons.includes(season)
        ? prev.seasons.filter(s => s !== season)
        : [...prev.seasons, season]
    }));
  };

  // Application Product handlers
  const resetAppProductForm = () => {
    setAppProductForm({
      name: '', active_ingredient: '', manufacturer: '', description: '',
      application_rate_min: '', application_rate_max: '', rate_unit: 'oz',
      target_gdd_interval: '', reapplication_interval_days: '',
      cost_per_unit: '', unit_size: '', affiliate_link: '',
      epa_registration_number: '', product_type: 'Pre-Emergent'
    });
    setEditingAppProduct(null);
  };

  const handleEditAppProduct = (product, category) => {
    setEditingAppProduct({ ...product, category });
    setAppProductForm({
      name: product.name || '',
      active_ingredient: product.active_ingredient || '',
      manufacturer: product.manufacturer || '',
      description: product.description || '',
      application_rate_min: product.application_rate_min?.toString() || '',
      application_rate_max: product.application_rate_max?.toString() || '',
      rate_unit: product.rate_unit || 'oz',
      target_gdd_interval: product.target_gdd_interval?.toString() || '',
      reapplication_interval_days: product.reapplication_interval_days?.toString() || '',
      cost_per_unit: product.cost_per_unit?.toString() || '',
      unit_size: product.unit_size || '',
      affiliate_link: product.affiliate_link || '',
      epa_registration_number: product.epa_registration_number || '',
      product_type: product.product_type || 'Pre-Emergent'
    });
    setShowAppProductDialog(true);
  };

  const handleSaveAppProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: appProductForm.name,
        active_ingredient: appProductForm.active_ingredient,
        manufacturer: appProductForm.manufacturer,
        description: appProductForm.description,
        application_rate_min: parseFloat(appProductForm.application_rate_min) || 0,
        application_rate_max: parseFloat(appProductForm.application_rate_max) || 0,
        rate_unit: appProductForm.rate_unit,
        target_gdd_interval: parseFloat(appProductForm.target_gdd_interval) || 0,
        reapplication_interval_days: parseInt(appProductForm.reapplication_interval_days) || 0,
        cost_per_unit: parseFloat(appProductForm.cost_per_unit) || null,
        unit_size: appProductForm.unit_size || null,
        affiliate_link: appProductForm.affiliate_link || null,
        epa_registration_number: appProductForm.epa_registration_number || null,
        product_type: appProductForm.product_type
      };

      const category = editingAppProduct ? editingAppProduct.category : activeProductCategory;
      
      if (editingAppProduct) {
        await axios.put(`${API_URL}/api/${category}/products/${editingAppProduct.id}`, payload, { withCredentials: true });
        toast.success('Product updated!');
      } else {
        await axios.post(`${API_URL}/api/${category}/products`, payload, { withCredentials: true });
        toast.success('Product created!');
      }
      
      setShowAppProductDialog(false);
      resetAppProductForm();
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteAppProduct = async (productId, category) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/${category}/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // CSV Import/Export handlers
  const handleCsvImport = async () => {
    if (!csvImportData.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    
    setCsvImporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/products/import/${csvImportCategory}`,
        { csv_data: csvImportData },
        { withCredentials: true }
      );
      
      const { imported, updated, errors } = response.data;
      toast.success(`Import complete: ${imported} new, ${updated} updated`);
      
      if (errors?.length > 0) {
        console.warn('Import errors:', errors);
        toast.warning(`${errors.length} rows had errors`);
      }
      
      setShowCsvImportDialog(false);
      setCsvImportData('');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setCsvImporting(false);
    }
  };

  const handleCsvExport = async (category) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/products/export/${category}`,
        { withCredentials: true }
      );
      
      // Create download
      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${category}_products_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} ${category} products`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // User tier management handlers
  const handleEditUserTier = (u) => {
    setEditingUserTier(u);
    setUserTierForm({
      tier: u.subscription_tier || 'free',
      expires_at: u.subscription_expires ? new Date(u.subscription_expires).toISOString().slice(0, 16) : ''
    });
    setShowUserTierDialog(true);
  };

  const handleSaveUserTier = async (e) => {
    e.preventDefault();
    if (!editingUserTier) return;
    
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(editingUserTier.email)}/tier`,
        {
          tier: userTierForm.tier,
          expires_at: userTierForm.expires_at ? new Date(userTierForm.expires_at).toISOString() : null
        },
        { withCredentials: true }
      );
      toast.success(`${editingUserTier.email} tier updated!`);
      setShowUserTierDialog(false);
      setEditingUserTier(null);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user tier');
    }
  };

  // User profile editing handlers (grass type, role)
  const handleEditUserProfile = (u) => {
    setEditingUserProfile(u);
    setUserProfileForm({
      grass_type: u.grass_type || 'cool-season',
      role: u.role || 'user'
    });
    setShowUserProfileDialog(true);
  };

  const handleSaveUserProfile = async (e) => {
    e.preventDefault();
    if (!editingUserProfile) return;
    
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(editingUserProfile.email)}/profile`,
        {
          grass_type: userProfileForm.grass_type,
          role: userProfileForm.role
        },
        { withCredentials: true }
      );
      toast.success(`${editingUserProfile.email} profile updated!`);
      setShowUserProfileDialog(false);
      setEditingUserProfile(null);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user profile');
    }
  };

  const resetAnnouncementForm = () => {
    setAnnTitle('');
    setAnnMessage('');
    setAnnType('info');
    setAnnLinkUrl('');
    setAnnLinkText('');
    setAnnDismissible(true);
    setAnnActive(true);
    setAnnScheduledStart('');
    setAnnScheduledEnd('');
    setEditingAnnouncement(null);
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: annTitle,
        message: annMessage,
        type: annType,
        link_url: annLinkUrl || null,
        link_text: annLinkText || null,
        dismissible: annDismissible,
        active: annActive,
        scheduled_start: annScheduledStart ? new Date(annScheduledStart).toISOString() : null,
        scheduled_end: annScheduledEnd ? new Date(annScheduledEnd).toISOString() : null
      };

      if (editingAnnouncement) {
        await axios.put(`${API_URL}/api/admin/announcements/${editingAnnouncement.id}`, payload, { withCredentials: true });
        toast.success('Announcement updated!');
      } else {
        await axios.post(`${API_URL}/api/admin/announcements`, payload, { withCredentials: true });
        toast.success('Announcement created!');
      }
      
      setShowAnnouncementDialog(false);
      resetAnnouncementForm();
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnTitle(announcement.title || '');
    setAnnMessage(announcement.message || '');
    setAnnType(announcement.type || 'info');
    setAnnLinkUrl(announcement.link_url || '');
    setAnnLinkText(announcement.link_text || '');
    setAnnDismissible(announcement.dismissible !== false);
    setAnnActive(announcement.active !== false);
    setAnnScheduledStart(announcement.scheduled_start ? new Date(announcement.scheduled_start).toISOString().slice(0, 16) : '');
    setAnnScheduledEnd(announcement.scheduled_end ? new Date(announcement.scheduled_end).toISOString().slice(0, 16) : '');
    setShowAnnouncementDialog(true);
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/announcements/${announcementId}`, { withCredentials: true });
      toast.success('Announcement deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleAnnouncementActive = async (announcement) => {
    try {
      await axios.put(`${API_URL}/api/admin/announcements/${announcement.id}`, 
        { active: !announcement.active }, 
        { withCredentials: true }
      );
      toast.success(announcement.active ? 'Announcement deactivated' : 'Announcement activated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const getAnnouncementTypeIcon = (type) => {
    switch (type) {
      case 'warning': return <Warning weight="bold" size={18} className="text-amber-500" />;
      case 'maintenance': return <Wrench weight="bold" size={18} className="text-blue-500" />;
      case 'success': return <CheckCircle weight="bold" size={18} className="text-green-500" />;
      default: return <Info weight="bold" size={18} className="text-primary" />;
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/api/products`,
        {
          name: productName,
          description: productDesc,
          affiliate_link: affiliateLink,
          image_url: imageUrl || null,
          product_type: productType,
          mark_text: markText || null
        },
        { withCredentials: true }
      );
      toast.success('Product created successfully!');
      setShowProductDialog(false);
      setProductName('');
      setProductDesc('');
      setAffiliateLink('');
      setImageUrl('');
      setMarkText('');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted successfully!');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handlePartnerAction = async (partnerId, action) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/partners/${partnerId}/${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Partner ${action}d successfully!`);
      fetchAdminData();
    } catch (error) {
      toast.error(`Failed to ${action} partner`);
    }
  };

  const handleAddPartner = async () => {
    if (!newPartner.business_name) { toast.error('Business name is required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/partners/add`, newPartner, { withCredentials: true });
      toast.success('Partner added successfully!');
      setShowAddPartner(false);
      setNewPartner({ business_name: '', contact_name: '', email: '', phone: '', service_type: '', area_served: '', description: '', website: '' });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add partner');
    }
  };

  const handleEditPartner = async () => {
    if (!editingPartner) return;
    if (!editingPartner.business_name) { toast.error('Business name is required'); return; }
    setPartnerSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/partners/${editingPartner.id}`,
        {
          business_name: editingPartner.business_name || '',
          contact_name: editingPartner.contact_name || '',
          email: editingPartner.email || '',
          phone: editingPartner.phone || '',
          service_type: editingPartner.service_type || '',
          area_served: editingPartner.area_served || '',
          description: editingPartner.description || '',
          website: editingPartner.website || '',
        },
        { withCredentials: true }
      );
      toast.success('Partner updated');
      setEditingPartner(null);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update partner');
    } finally {
      setPartnerSaving(false);
    }
  };

  const handleDeletePartner = async (partner) => {
    if (!window.confirm(`Delete "${partner.business_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/partners/${partner.id}`, { withCredentials: true });
      toast.success('Partner deleted');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete partner');
    }
  };

  const handleUpgradeUser = async (email) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/upgrade`, {}, { withCredentials: true });
      toast.success(`${email} upgraded to Premium!`);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade user');
    }
  };

  const handleDowngradeUser = async (email) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/downgrade`, {}, { withCredentials: true });
      toast.success(`${email} downgraded to Basic`);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to downgrade user');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'pgr': return <Plant weight="duotone" size={20} className="text-green-600" />;
      case 'weed': return <Leaf weight="duotone" size={20} className="text-amber-600" />;
      case 'pest': return <Bug weight="duotone" size={20} className="text-red-600" />;
      case 'disease': return <Virus weight="duotone" size={20} className="text-purple-600" />;
      default: return <Flask weight="duotone" size={20} />;
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'enthusiast_annual': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'homeowner_seasonal': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div data-testid="admin-container" className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background/70 backdrop-blur-xl border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 md:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <button
                data-testid="admin-hamburger-btn"
                onClick={() => setDrawerOpen(true)}
                className="p-2 -ml-1 rounded-lg hover:bg-secondary/50 transition-colors"
                aria-label="Open admin navigation"
              >
                <List weight="bold" size={22} />
              </button>
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                <TurfPulseLogoSVG animated={false} size={28} />
                <span className="hidden sm:block text-base font-bold">Admin Panel</span>
              </div>
              <div className="hidden md:block ml-3 pl-3 border-l border-border/60">
                <VersionBadge />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/app')} data-testid="admin-back-btn">
              <ArrowLeft weight="bold" size={16} className="mr-1.5" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Admin Navigation Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 overflow-y-auto">
          <SheetHeader className="px-4 pt-5 pb-3 border-b border-border">
            <div className="flex items-center gap-2">
              <TurfPulseLogoSVG animated={false} size={28} />
              <div>
                <SheetTitle className="text-base font-bold leading-tight">Admin Panel</SheetTitle>
                <SheetDescription className="text-xs">TurfPulse Management</SheetDescription>
              </div>
            </div>
          </SheetHeader>
          <nav className="py-2">
            {[
              { id: 'app-products', label: 'Products', icon: <Flask weight="bold" size={18} /> },
              { id: 'users', label: 'Users', icon: <Users weight="bold" size={18} /> },
              {
                id: 'founders',
                label: 'Founders',
                icon: <Crown weight="bold" size={18} />,
                navTo: '/admin/founders',
                badge: foundersStats.count !== null ? `${foundersStats.count}/${foundersStats.cap}` : null,
              },
              {
                id: 'journal',
                label: 'Journal',
                icon: <Notebook weight="bold" size={18} />,
                navTo: '/admin/journal',
              },
              { id: 'analytics', label: 'Analytics', icon: <ChartBar weight="bold" size={18} /> },
              { id: 'irrigation', label: 'Irrigation', icon: <span className="text-base">💧</span> },
              { id: 'announcements', label: 'Announcements', icon: <Megaphone weight="bold" size={18} /> },
              { id: 'marketing', label: 'Marketing', icon: <Palette weight="bold" size={18} /> },
              { id: 'partners', label: 'Partners', icon: <Handshake weight="bold" size={18} /> },
              { id: 'affiliate', label: 'Affiliate', icon: <Link weight="bold" size={18} /> },
              { id: 'roadmap', label: 'Roadmap', icon: <Target weight="bold" size={18} /> },
              { id: 'videos', label: 'Videos', icon: <span className="text-base">🎬</span> },
              { id: 'engagement', label: 'Engagement', icon: <span className="text-base">📊</span> },
              { id: 'tracker', label: 'Business Tracker', icon: <CurrencyDollar weight="bold" size={18} /> },
              { id: 'calibration', label: 'AI Calibration', icon: <Target weight="bold" size={18} /> },
              { id: 'nfc', label: 'NFC Redirect', icon: <Broadcast weight="bold" size={18} /> },
              { id: 'manual', label: 'Ops Manual', icon: <FileText weight="bold" size={18} /> },
              { id: 'vendor-assets', label: 'Vendor Assets', icon: <TShirt weight="bold" size={18} /> },
              { id: 'asset-hub', label: 'Asset Hub', icon: <Palette weight="bold" size={18} />, navTo: '/admin/assets' },
              { id: 'shiplog', label: 'Ship Log', icon: <TrendUp weight="bold" size={18} />, navTo: '/admin/shiplog' },
              { id: 'console', label: 'Console (Live)', icon: <Broadcast weight="bold" size={18} />, navTo: '/console' },
              { id: 'nurture', label: 'Weekly Pulse Emails', icon: <Megaphone weight="bold" size={18} />, navTo: '/admin/nurture' },
              { id: 'campaigns', label: 'Email Campaigns', icon: <Megaphone weight="bold" size={18} />, navTo: '/admin/campaigns' },
              { id: 'blog', label: 'Blog Admin', icon: <FileText weight="bold" size={18} />, navTo: '/admin/blog' },
            ].map(item => (
              <button
                key={item.id}
                data-testid={`admin-nav-${item.id}`}
                onClick={() => {
                  if (item.navTo) {
                    navigate(item.navTo);
                  } else if (item.id === 'vendor-assets') {
                    navigate('/vendor-assets');
                  } else {
                    setActiveSection(item.id);
                  }
                  setDrawerOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                  ${activeSection === item.id
                    ? 'bg-primary/10 text-primary font-semibold border-l-2 border-primary'
                    : 'text-foreground/80 hover:bg-secondary/50 hover:text-foreground'
                  }`}
              >
                <span className="w-5 flex justify-center">{item.icon}</span>
                <span>{item.label}</span>
                {item.badge && (
                  <span className="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-6 gap-4">
          <Card data-testid="stat-total-users">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Users weight="duotone" size={20} className="text-primary" aria-hidden="true" />
                <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-premium-users">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Premium</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Crown weight="duotone" size={20} className="text-amber-500" aria-hidden="true" />
                <div className="text-2xl font-bold">{stats?.premium_users || 0}</div>
              </div>
              <p className="text-xs text-muted-foreground">{stats?.conversion_rate || 0}% conv.</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-mrr">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">MRR</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CurrencyDollar weight="duotone" size={20} className="text-green-500" aria-hidden="true" />
                <div className="text-2xl font-bold">${stats?.mrr || '0.00'}</div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-applications">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Leaf weight="duotone" size={20} className="text-primary" aria-hidden="true" />
                <div className="text-2xl font-bold">{stats?.total_applications || 0}</div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-products">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Products</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Flask weight="duotone" size={20} className="text-purple-500" aria-hidden="true" />
                <div className="text-2xl font-bold">{applicationProducts.counts?.total || 0}</div>
              </div>
              <p className="text-xs text-muted-foreground">in catalog</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-irrigation">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Irrigation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span className="text-xl">💧</span>
                <div className="text-2xl font-bold">{stats?.irrigation_connected || 0}</div>
              </div>
              <p className="text-xs text-muted-foreground">connected</p>
            </CardContent>
          </Card>
        </div>

        {/* Founders Pulse — live count + recent signups */}
        <FoundersPulseWidget />

        {/* Priorities of the Week */}
        <PrioritiesWidget />

        {/* System Health Widget */}
        <SystemHealthWidget />

        {/* Tabs for different sections */}
        <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
          <TabsList className="hidden">
            <TabsTrigger value="app-products" />
            <TabsTrigger value="users" />
            <TabsTrigger value="analytics" />
            <TabsTrigger value="irrigation" />
            <TabsTrigger value="announcements" />
            <TabsTrigger value="marketing" />
            <TabsTrigger value="partners" />
            <TabsTrigger value="affiliate" />
            <TabsTrigger value="roadmap" />
            <TabsTrigger value="videos" />
            <TabsTrigger value="engagement" />
            <TabsTrigger value="tracker" />
            <TabsTrigger value="calibration" />
            <TabsTrigger value="nfc" />
            <TabsTrigger value="manual" />
          </TabsList>

          {/* Application Products Tab - FULL CRUD */}
          <TabsContent value="app-products">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Application Product Catalog</CardTitle>
                    <CardDescription>Manage PGR, Weed Control, Pest, and Disease products with EPA info and GDD intervals</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {/* CSV Import Dialog */}
                    <Dialog open={showCsvImportDialog} onOpenChange={setShowCsvImportDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline" data-testid="csv-import-btn">
                          <UploadSimple weight="bold" size={18} className="mr-2" />
                          Import CSV
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Import Products from CSV</DialogTitle>
                          <DialogDescription>
                            Bulk import products. Existing products (matched by name) will be updated.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label>Target Category</Label>
                            <Select value={csvImportCategory} onValueChange={setCsvImportCategory}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pgr">PGR Products</SelectItem>
                                <SelectItem value="weed">Weed Control</SelectItem>
                                <SelectItem value="pest">Pest Management</SelectItem>
                                <SelectItem value="disease">Disease Management</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>CSV Data (paste below)</Label>
                            <Textarea
                              value={csvImportData}
                              onChange={(e) => setCsvImportData(e.target.value)}
                              placeholder="name,active_ingredient,manufacturer,description,product_type,application_rate_min,application_rate_max,rate_unit,target_gdd_interval,reapplication_interval_days,cost_per_unit,unit_size,affiliate_link,epa_registration_number"
                              rows={8}
                              className="font-mono text-xs"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              First row must be header. Required: name, active_ingredient, manufacturer
                            </p>
                          </div>
                          <div className="bg-muted/50 rounded-lg p-3">
                            <p className="text-xs font-medium mb-1">Example CSV:</p>
                            <code className="text-xs block whitespace-pre-wrap">
name,active_ingredient,manufacturer,description,epa_registration_number{"\n"}
Primo Maxx,Trinexapac-ethyl,Syngenta,Industry-standard PGR,100-1234
                            </code>
                          </div>
                          <div className="flex justify-between">
                            <Button variant="outline" onClick={() => handleCsvExport(csvImportCategory)}>
                              <DownloadSimple weight="bold" size={16} className="mr-2" />
                              Download Current {csvImportCategory.toUpperCase()} as CSV
                            </Button>
                            <div className="flex gap-2">
                              <Button variant="outline" onClick={() => setShowCsvImportDialog(false)}>Cancel</Button>
                              <Button onClick={handleCsvImport} disabled={csvImporting}>
                                {csvImporting ? 'Importing...' : 'Import'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Add Product Dialog */}
                    <Dialog open={showAppProductDialog} onOpenChange={(open) => {
                      setShowAppProductDialog(open);
                      if (!open) resetAppProductForm();
                    }}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-app-product-btn">
                          <Plus weight="bold" size={18} className="mr-2" />
                          Add Product
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{editingAppProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                          <DialogDescription>
                            {editingAppProduct ? `Editing ${editingAppProduct.name}` : `Adding to ${activeProductCategory.toUpperCase()} category`}
                          </DialogDescription>
                        </DialogHeader>
                      <form onSubmit={handleSaveAppProduct} className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <Label htmlFor="app-product-name">Product Name *</Label>
                            <Input
                              id="app-product-name"
                              data-testid="app-product-name-input"
                              value={appProductForm.name}
                              onChange={(e) => setAppProductForm(p => ({ ...p, name: e.target.value }))}
                              placeholder="e.g., Primo Maxx"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="active-ingredient">Active Ingredient *</Label>
                            <Input
                              id="active-ingredient"
                              value={appProductForm.active_ingredient}
                              onChange={(e) => setAppProductForm(p => ({ ...p, active_ingredient: e.target.value }))}
                              placeholder="e.g., Trinexapac-ethyl"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="manufacturer">Manufacturer *</Label>
                            <Input
                              id="manufacturer"
                              value={appProductForm.manufacturer}
                              onChange={(e) => setAppProductForm(p => ({ ...p, manufacturer: e.target.value }))}
                              placeholder="e.g., Syngenta"
                              required
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                              id="description"
                              value={appProductForm.description}
                              onChange={(e) => setAppProductForm(p => ({ ...p, description: e.target.value }))}
                              placeholder="Product description..."
                              rows={2}
                            />
                          </div>
                          <div>
                            <Label htmlFor="epa-reg">EPA Registration Number</Label>
                            <Input
                              id="epa-reg"
                              data-testid="epa-registration-input"
                              value={appProductForm.epa_registration_number}
                              onChange={(e) => setAppProductForm(p => ({ ...p, epa_registration_number: e.target.value }))}
                              placeholder="e.g., 100-1234"
                            />
                          </div>
                          <div>
                            <Label htmlFor="product-type">Product Type</Label>
                            <Select value={appProductForm.product_type} onValueChange={(v) => setAppProductForm(p => ({ ...p, product_type: v }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Pre-Emergent">Pre-Emergent</SelectItem>
                                <SelectItem value="Post-Emergent">Post-Emergent</SelectItem>
                                <SelectItem value="PGR">PGR</SelectItem>
                                <SelectItem value="Fungicide">Fungicide</SelectItem>
                                <SelectItem value="Insecticide">Insecticide</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="rate-min">Application Rate Min</Label>
                            <Input
                              id="rate-min"
                              type="number"
                              step="0.01"
                              value={appProductForm.application_rate_min}
                              onChange={(e) => setAppProductForm(p => ({ ...p, application_rate_min: e.target.value }))}
                              placeholder="0.25"
                            />
                          </div>
                          <div>
                            <Label htmlFor="rate-max">Application Rate Max</Label>
                            <Input
                              id="rate-max"
                              type="number"
                              step="0.01"
                              value={appProductForm.application_rate_max}
                              onChange={(e) => setAppProductForm(p => ({ ...p, application_rate_max: e.target.value }))}
                              placeholder="0.75"
                            />
                          </div>
                          <div>
                            <Label htmlFor="rate-unit">Rate Unit</Label>
                            <Select value={appProductForm.rate_unit} onValueChange={(v) => setAppProductForm(p => ({ ...p, rate_unit: v }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="oz">oz / 1000 sq ft</SelectItem>
                                <SelectItem value="lb">lb / 1000 sq ft</SelectItem>
                                <SelectItem value="fl oz">fl oz / 1000 sq ft</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="gdd-interval">Target GDD Interval</Label>
                            <Input
                              id="gdd-interval"
                              type="number"
                              step="1"
                              value={appProductForm.target_gdd_interval}
                              onChange={(e) => setAppProductForm(p => ({ ...p, target_gdd_interval: e.target.value }))}
                              placeholder="250"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reapp-days">Reapplication Days</Label>
                            <Input
                              id="reapp-days"
                              type="number"
                              value={appProductForm.reapplication_interval_days}
                              onChange={(e) => setAppProductForm(p => ({ ...p, reapplication_interval_days: e.target.value }))}
                              placeholder="14"
                            />
                          </div>
                          <div>
                            <Label htmlFor="cost">Cost Per Unit ($)</Label>
                            <Input
                              id="cost"
                              type="number"
                              step="0.01"
                              value={appProductForm.cost_per_unit}
                              onChange={(e) => setAppProductForm(p => ({ ...p, cost_per_unit: e.target.value }))}
                              placeholder="289.99"
                            />
                          </div>
                          <div>
                            <Label htmlFor="unit-size">Unit Size</Label>
                            <Input
                              id="unit-size"
                              value={appProductForm.unit_size}
                              onChange={(e) => setAppProductForm(p => ({ ...p, unit_size: e.target.value }))}
                              placeholder="1 gallon"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="affiliate-link">Affiliate/Purchase Link</Label>
                            <Input
                              id="affiliate-link"
                              type="url"
                              value={appProductForm.affiliate_link}
                              onChange={(e) => setAppProductForm(p => ({ ...p, affiliate_link: e.target.value }))}
                              placeholder="https://amazon.com/dp/..."
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => {
                            setShowAppProductDialog(false);
                            resetAppProductForm();
                          }}>
                            Cancel
                          </Button>
                          <Button type="submit" data-testid="save-app-product-btn">
                            {editingAppProduct ? 'Update' : 'Create'} Product
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Category Tabs */}
                <Tabs value={activeProductCategory} onValueChange={setActiveProductCategory} className="w-full">
                  <TabsList className="grid w-full grid-cols-4 mb-4">
                    <TabsTrigger value="pgr" data-testid="products-pgr-tab">
                      <Plant weight="bold" size={16} className="mr-1.5 text-green-600" />
                      PGR ({applicationProducts.counts?.pgr || 0})
                    </TabsTrigger>
                    <TabsTrigger value="weed" data-testid="products-weed-tab">
                      <Leaf weight="bold" size={16} className="mr-1.5 text-amber-600" />
                      Weed ({applicationProducts.counts?.weed || 0})
                    </TabsTrigger>
                    <TabsTrigger value="pest" data-testid="products-pest-tab">
                      <Bug weight="bold" size={16} className="mr-1.5 text-red-600" />
                      Pest ({applicationProducts.counts?.pest || 0})
                    </TabsTrigger>
                    <TabsTrigger value="disease" data-testid="products-disease-tab">
                      <Virus weight="bold" size={16} className="mr-1.5 text-purple-600" />
                      Disease ({applicationProducts.counts?.disease || 0})
                    </TabsTrigger>
                  </TabsList>

                  {['pgr', 'weed', 'pest', 'disease'].map(cat => (
                    <TabsContent key={cat} value={cat}>
                      <div className="space-y-3 max-h-[500px] overflow-y-auto">
                        {applicationProducts[cat]?.length > 0 ? (
                          applicationProducts[cat].map((product) => (
                            <div key={product.id} data-testid={`product-row-${product.id}`} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    {getCategoryIcon(cat)}
                                    <h4 className="font-bold">{product.name}</h4>
                                    {product.epa_registration_number && (
                                      <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded">
                                        EPA: {product.epa_registration_number}
                                      </span>
                                    )}
                                    {product.product_type && (
                                      <span className="text-xs bg-secondary px-2 py-0.5 rounded">
                                        {product.product_type}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground mb-2">{product.description}</p>
                                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                    <span><strong>AI:</strong> {product.active_ingredient}</span>
                                    <span><strong>Mfr:</strong> {product.manufacturer}</span>
                                    {product.target_gdd_interval > 0 && (
                                      <span><strong>GDD:</strong> {product.target_gdd_interval}</span>
                                    )}
                                    {product.reapplication_interval_days > 0 && (
                                      <span><strong>Reapp:</strong> {product.reapplication_interval_days} days</span>
                                    )}
                                    {product.cost_per_unit && (
                                      <span><strong>Cost:</strong> ${product.cost_per_unit}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditAppProduct(product, cat)}
                                    data-testid={`edit-product-${product.id}`}
                                  >
                                    <PencilSimple weight="bold" size={16} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteAppProduct(product.id, cat)}
                                    data-testid={`delete-product-${product.id}`}
                                  >
                                    <Trash weight="bold" size={16} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            {getCategoryIcon(cat)}
                            <p className="mt-2">No {cat.toUpperCase()} products yet</p>
                            <Button variant="outline" size="sm" className="mt-2" onClick={() => setShowAppProductDialog(true)}>
                              Add First Product
                            </Button>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab - Enhanced with Tier Management */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>View and manage all registered users — set subscription tiers and expiration dates</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* User Tier Dialog */}
                <Dialog open={showUserTierDialog} onOpenChange={setShowUserTierDialog}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Set Subscription Tier</DialogTitle>
                      <DialogDescription>
                        {editingUserTier?.email}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSaveUserTier} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="tier-select">Subscription Tier</Label>
                        <Select value={userTierForm.tier} onValueChange={(v) => setUserTierForm(f => ({ ...f, tier: v }))}>
                          <SelectTrigger data-testid="tier-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {subscriptionTiers.map(t => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="tier-expires">Expiration Date (optional)</Label>
                        <Input
                          id="tier-expires"
                          type="datetime-local"
                          value={userTierForm.expires_at}
                          onChange={(e) => setUserTierForm(f => ({ ...f, expires_at: e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave blank for no expiration</p>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowUserTierDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="save-user-tier-btn">
                          Save Tier
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* User Profile Dialog (Grass Type & Role) */}
                <Dialog open={showUserProfileDialog} onOpenChange={setShowUserProfileDialog}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit User Profile</DialogTitle>
                      <DialogDescription>
                        {editingUserProfile?.email}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSaveUserProfile} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="profile-grass-type">Grass Type</Label>
                        <Select value={userProfileForm.grass_type} onValueChange={(v) => setUserProfileForm(f => ({ ...f, grass_type: v }))}>
                          <SelectTrigger data-testid="profile-grass-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cool-season">Cool-Season</SelectItem>
                            <SelectItem value="warm-season">Warm-Season</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Kentucky Bluegrass, Fescue = Cool | Bermuda, St. Augustine = Warm</p>
                      </div>
                      <div>
                        <Label htmlFor="profile-role">Role</Label>
                        <Select value={userProfileForm.role} onValueChange={(v) => setUserProfileForm(f => ({ ...f, role: v }))}>
                          <SelectTrigger data-testid="profile-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Admins have full access to the admin panel</p>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowUserProfileDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="save-user-profile-btn">
                          Save Profile
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Search & Sort Bar */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="user-search-input"
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={userSortBy} onValueChange={setUserSortBy}>
                    <SelectTrigger data-testid="user-sort-select" className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest First</SelectItem>
                      <SelectItem value="oldest">Oldest First</SelectItem>
                      <SelectItem value="name-az">Name A-Z</SelectItem>
                      <SelectItem value="name-za">Name Z-A</SelectItem>
                      <SelectItem value="email-az">Email A-Z</SelectItem>
                      <SelectItem value="tier-high">Tier: High to Low</SelectItem>
                      <SelectItem value="tier-low">Tier: Low to High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {userSearchQuery && (
                  <p className="text-xs text-muted-foreground mb-3">
                    Showing {users.filter(u => 
                      u.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                      u.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
                    ).length} of {users.length} users
                  </p>
                )}

                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {users
                    .filter(u => {
                      if (!userSearchQuery) return true;
                      const query = userSearchQuery.toLowerCase();
                      return (
                        u.name?.toLowerCase().includes(query) ||
                        u.email?.toLowerCase().includes(query)
                      );
                    })
                    .sort((a, b) => {
                      const tierOrder = { 'enthusiast_annual': 3, 'homeowner_seasonal': 2, 'free': 1, undefined: 0 };
                      switch (userSortBy) {
                        case 'newest':
                          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                        case 'oldest':
                          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                        case 'name-az':
                          return (a.name || '').localeCompare(b.name || '');
                        case 'name-za':
                          return (b.name || '').localeCompare(a.name || '');
                        case 'email-az':
                          return (a.email || '').localeCompare(b.email || '');
                        case 'tier-high':
                          return (tierOrder[b.subscription_tier] || 0) - (tierOrder[a.subscription_tier] || 0);
                        case 'tier-low':
                          return (tierOrder[a.subscription_tier] || 0) - (tierOrder[b.subscription_tier] || 0);
                        default:
                          return 0;
                      }
                    })
                    .map((u) => (
                    <div key={u.email} data-testid={`user-row-${u.email}`} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold">{u.name}</h4>
                            {u.premium && (
                              <Crown weight="fill" size={16} className="text-amber-500" aria-label="Premium user" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                          <div className="flex gap-2 mt-2 flex-wrap">
                            <span className="text-xs bg-secondary px-2 py-1 rounded">
                              {u.grass_type}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${getTierBadgeColor(u.subscription_tier)}`}>
                              {u.tier_name || 'Free'}
                            </span>
                            {u.role === 'admin' && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                Admin
                              </span>
                            )}
                            {u.subscription_expires && (
                              <span className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-1 rounded">
                                Expires: {new Date(u.subscription_expires).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <p className="text-xs text-muted-foreground">
                            Joined {new Date(u.created_at).toLocaleDateString()}
                          </p>
                          <div className="flex gap-2 flex-wrap justify-end">
                            {/* Edit Profile button - always visible */}
                            <Button
                              data-testid={`edit-profile-btn-${u.email}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUserProfile(u)}
                              className="text-xs"
                            >
                              <Gear weight="bold" size={14} className="mr-1" />
                              Edit Profile
                            </Button>
                            {u.role !== 'admin' && (
                              <>
                                <Button
                                  data-testid={`edit-tier-btn-${u.email}`}
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditUserTier(u)}
                                  className="text-xs"
                                >
                                  <PencilSimple weight="bold" size={14} className="mr-1" />
                                  Set Tier
                                </Button>
                                {u.premium ? (
                                  <Button
                                    data-testid={`downgrade-btn-${u.email}`}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDowngradeUser(u.email)}
                                    className="text-xs"
                                  >
                                    Downgrade
                                  </Button>
                                ) : (
                                  <Button
                                    data-testid={`upgrade-btn-${u.email}`}
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleUpgradeUser(u.email)}
                                    className="text-xs"
                                  >
                                    <Crown weight="bold" size={14} className="mr-1" aria-hidden="true" />
                                    Upgrade
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab - Campaign Tracking */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target weight="duotone" size={24} />
                  Campaign Analytics
                </CardTitle>
                <CardDescription>Track which marketing campaigns drive signups — use UTM parameters in your links</CardDescription>
              </CardHeader>
              <CardContent>
                {campaignAnalytics ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-secondary/50 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold">{campaignAnalytics.summary.total_users}</p>
                        <p className="text-sm text-muted-foreground">Total Users</p>
                      </div>
                      <div className="bg-green-500/10 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-600">{campaignAnalytics.summary.tracked_signups}</p>
                        <p className="text-sm text-muted-foreground">Tracked Signups</p>
                      </div>
                      <div className="bg-secondary/50 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold">{campaignAnalytics.summary.organic_signups}</p>
                        <p className="text-sm text-muted-foreground">Organic (No UTM)</p>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold">{campaignAnalytics.summary.tracking_rate}%</p>
                        <p className="text-sm text-muted-foreground">Tracking Rate</p>
                      </div>
                    </div>

                    {/* Campaign Performance Table */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <TrendUp weight="bold" size={20} />
                        Campaign Performance
                      </h3>
                      {campaignAnalytics.campaigns.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-secondary/50">
                              <tr>
                                <th className="text-left p-3 font-medium">Campaign</th>
                                <th className="text-left p-3 font-medium">Source</th>
                                <th className="text-left p-3 font-medium">Medium</th>
                                <th className="text-center p-3 font-medium">Signups</th>
                                <th className="text-center p-3 font-medium">Premium</th>
                                <th className="text-center p-3 font-medium">Conv. Rate</th>
                                <th className="text-left p-3 font-medium">Last Signup</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaignAnalytics.campaigns.map((c, idx) => (
                                <tr key={idx} className="border-t hover:bg-secondary/30">
                                  <td className="p-3 font-medium">{c.campaign}</td>
                                  <td className="p-3">{c.source}</td>
                                  <td className="p-3">{c.medium}</td>
                                  <td className="p-3 text-center">
                                    <span className="bg-green-500/20 text-green-700 dark:text-green-400 px-2 py-0.5 rounded font-medium">
                                      {c.signups}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    {c.premium_conversions > 0 && (
                                      <span className="bg-amber-500/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded">
                                        {c.premium_conversions}
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-center">{c.conversion_rate}%</td>
                                  <td className="p-3 text-muted-foreground text-xs">
                                    {c.last_signup ? new Date(c.last_signup).toLocaleDateString() : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground border rounded-lg">
                          <ChartBar weight="duotone" size={48} className="mx-auto mb-4 opacity-50" />
                          <p className="font-medium">No campaign data yet</p>
                          <p className="text-sm mt-1">Signups with UTM parameters will appear here</p>
                        </div>
                      )}
                    </div>

                    {/* UTM Link Generator */}
                    <div className="bg-secondary/30 rounded-lg p-4">
                      <h3 className="font-semibold mb-2">How to Track Campaigns</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Add UTM parameters to your links. When users sign up, we'll track which campaign brought them.
                      </p>
                      <div className="bg-background rounded p-3 font-mono text-xs overflow-x-auto">
                        <code>
                          https://getturfpulse.com/?utm_source=<span className="text-blue-500">facebook</span>&utm_medium=<span className="text-green-500">social</span>&utm_campaign=<span className="text-amber-500">teaser1</span>
                        </code>
                      </div>
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><strong>utm_source:</strong> facebook, instagram, email</div>
                        <div><strong>utm_medium:</strong> social, cpc, email</div>
                        <div><strong>utm_campaign:</strong> teaser1, teaser2, reveal</div>
                        <div><strong>utm_content:</strong> video, image, link</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ChartBar weight="duotone" size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Loading analytics...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Irrigation Tab */}
          <TabsContent value="irrigation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  💧 Irrigation Integrations
                </CardTitle>
                <CardDescription>
                  Monitor connected smart irrigation systems • {irrigationIntegrations.length} active connection(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="text-2xl font-bold text-blue-600">{stats?.irrigation_connected || 0}</div>
                    <div className="text-sm text-muted-foreground">Total Connected</div>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <div className="text-2xl font-bold text-emerald-600">{stats?.rachio_connected || 0}</div>
                    <div className="text-sm text-muted-foreground">Rachio Users</div>
                  </div>
                  <div className="p-4 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                    <div className="text-2xl font-bold text-violet-600">{stats?.total_soil_tests || 0}</div>
                    <div className="text-sm text-muted-foreground">Soil Tests</div>
                  </div>
                </div>

                {/* Connected Users Table */}
                {irrigationIntegrations.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">User</th>
                          <th className="text-left py-2 px-3">Provider</th>
                          <th className="text-left py-2 px-3">Devices</th>
                          <th className="text-left py-2 px-3">Connected</th>
                          <th className="text-left py-2 px-3">Last Sync</th>
                          <th className="text-left py-2 px-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {irrigationIntegrations.map((integration, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="py-2 px-3 font-medium">{integration.user_email}</td>
                            <td className="py-2 px-3">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                {integration.provider === 'rachio' ? '🌧️ Rachio' : integration.provider}
                              </span>
                            </td>
                            <td className="py-2 px-3">{integration.devices?.length || 0} device(s)</td>
                            <td className="py-2 px-3 text-muted-foreground">
                              {integration.connected_at ? new Date(integration.connected_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-2 px-3 text-muted-foreground">
                              {integration.last_sync ? new Date(integration.last_sync).toLocaleString() : '-'}
                            </td>
                            <td className="py-2 px-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={async () => {
                                  if (!window.confirm(`Disconnect irrigation for ${integration.user_email}?`)) return;
                                  try {
                                    await axios.delete(`${API_URL}/api/admin/irrigation/${integration.user_id}`, { withCredentials: true });
                                    toast.success('Integration disconnected');
                                    fetchAdminData();
                                  } catch (err) {
                                    toast.error('Failed to disconnect');
                                  }
                                }}
                              >
                                Disconnect
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="text-4xl mb-2">💧</div>
                    <p>No irrigation integrations yet</p>
                    <p className="text-xs mt-1">Users can connect their Rachio from the Irrigation page</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing & Branding Hub Tab */}
          <TabsContent value="marketing">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Palette weight="duotone" size={24} />
                      Marketing & Branding Hub
                    </CardTitle>
                    <CardDescription>Access brand assets, marketing campaigns, and graphics — no GitHub needed</CardDescription>
                  </div>
                  <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-marketing-asset-btn">
                        <Plus weight="bold" size={18} className="mr-2" />
                        Add Asset
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Marketing Asset</DialogTitle>
                        <DialogDescription>Add a new graphic, campaign, or brand resource</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await axios.post(`${API_URL}/api/admin/marketing/assets`, assetForm, { withCredentials: true });
                          toast.success('Asset added!');
                          setShowAssetDialog(false);
                          setAssetForm({ name: '', description: '', category: 'graphic', url: '', type: 'image' });
                          fetchAdminData();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to add asset');
                        }
                      }} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="asset-name">Name *</Label>
                          <Input
                            id="asset-name"
                            value={assetForm.name}
                            onChange={(e) => setAssetForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="e.g., Facebook Teaser Image 1"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="asset-desc">Description</Label>
                          <Textarea
                            id="asset-desc"
                            value={assetForm.description}
                            onChange={(e) => setAssetForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="What is this asset used for?"
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="asset-category">Category</Label>
                            <Select value={assetForm.category} onValueChange={(v) => setAssetForm(f => ({ ...f, category: v }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="graphic">Graphic</SelectItem>
                                <SelectItem value="campaign">Campaign</SelectItem>
                                <SelectItem value="email">Email Template</SelectItem>
                                <SelectItem value="social">Social Media</SelectItem>
                                <SelectItem value="logo">Logo/Brand</SelectItem>
                                <SelectItem value="merch">Merchandise</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="asset-type">Type</Label>
                            <Select value={assetForm.type} onValueChange={(v) => setAssetForm(f => ({ ...f, type: v }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="image">Image</SelectItem>
                                <SelectItem value="document">Document</SelectItem>
                                <SelectItem value="link">Link</SelectItem>
                                <SelectItem value="text">Text/Copy</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="asset-url">URL / Content</Label>
                          <Input
                            id="asset-url"
                            value={assetForm.url}
                            onChange={(e) => setAssetForm(f => ({ ...f, url: e.target.value }))}
                            placeholder="https://... or paste content"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button type="button" variant="outline" onClick={() => setShowAssetDialog(false)}>Cancel</Button>
                          <Button type="submit">Add Asset</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {/* Brand Assets Section */}
                <div className="mb-8">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Image weight="duotone" size={20} className="text-primary" />
                    Brand Assets
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Primary Logo */}
                    <div className="border rounded-lg p-4 text-center">
                      <div className="bg-primary/10 rounded-lg p-4 mb-2 flex items-center justify-center">
                        <TurfPulseLogoSVG size={48} animated={false} />
                      </div>
                      <p className="text-sm font-medium">Primary Logo</p>
                      <p className="text-xs text-muted-foreground">EKG Circle Mark</p>
                      <Button size="sm" variant="ghost" className="mt-2 w-full" onClick={() => {
                        navigator.clipboard.writeText('<TurfPulseLogoSVG />');
                        toast.success('Component name copied!');
                      }}>
                        <Copy weight="bold" size={14} className="mr-1" />
                        Copy
                      </Button>
                    </div>
                    
                    {/* Brand Colors */}
                    <div className="border rounded-lg p-4">
                      <p className="text-sm font-medium mb-2">Brand Colors</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-[#0D9256]" />
                          <span className="text-xs font-mono">#0D9256</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-[#1a1a1a]" />
                          <span className="text-xs font-mono">#1A1A1A</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-[#F5F5F4]" />
                          <span className="text-xs font-mono">#F5F5F4</span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Links */}
                    <div className="border rounded-lg p-4">
                      <p className="text-sm font-medium mb-2">Quick Links</p>
                      <div className="space-y-2">
                        <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => window.open('/pitch.html', '_blank')}>
                          <FileText weight="bold" size={14} className="mr-2" />
                          Partner Pitch
                        </Button>
                        <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => window.open('/card.html', '_blank')}>
                          <Link weight="bold" size={14} className="mr-2" />
                          NFC Share Card
                        </Button>
                      </div>
                    </div>

                    {/* Merch Orders Note */}
                    <div className="border rounded-lg p-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-2 mb-2">
                        <TShirt weight="duotone" size={20} className="text-amber-600" />
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Merch Orders</p>
                      </div>
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        Coming soon — branded shirts, hats, and yard signs for partners.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step-by-Step Guide for Creating Marketing Assets */}
                <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-blue-800 dark:text-blue-300">
                    <Wrench weight="duotone" size={20} />
                    How to Create Polished Marketing Assets
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">1</div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Define Your Campaign Goal</p>
                        <p className="text-xs text-muted-foreground">What are you promoting? A feature, seasonal timing tip, or partner offer?</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Use Brand Colors & Logo</p>
                        <p className="text-xs text-muted-foreground">Primary green (#0D9256), dark (#1A1A1A), light (#F5F5F4). Always include the EKG logo.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">3</div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Generate Graphics with AI</p>
                        <p className="text-xs text-muted-foreground">Use Canva, Gemini, or DALL-E. Prompt: "Lush green lawn, morning dew, suburban backyard, professional photography"</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">4</div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Write Compelling Copy</p>
                        <p className="text-xs text-muted-foreground">Hook → Problem → Solution → CTA. Example: "Crabgrass winning? → Track GDD to hit the pre-emergent window perfectly."</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">5</div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Add UTM Tracking</p>
                        <p className="text-xs text-muted-foreground">getturfpulse.com?utm_source=facebook&utm_campaign=spring2026&utm_content=crabgrass</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">6</div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Upload & Organize Here</p>
                        <p className="text-xs text-muted-foreground">Click "Add Asset" above. Categorize properly (Graphic, Campaign, Social, etc.) for easy access.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      <strong>Pro Tip:</strong> Save both the source file (PSD/Canva link) and the exported image. 
                      Use consistent naming: <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">turfpulse_[platform]_[campaign]_[date].png</code>
                    </p>
                  </div>
                </div>

                {/* Marketing Assets Grid */}
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Megaphone weight="duotone" size={20} className="text-primary" />
                    Marketing Assets ({marketingAssets.length})
                  </h3>
                  {marketingAssets.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {marketingAssets.map((asset) => (
                        <div key={asset.id} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors" data-testid={`marketing-asset-${asset.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {asset.type === 'image' && <Image weight="duotone" size={18} className="text-blue-500" />}
                              {asset.type === 'document' && <FileText weight="duotone" size={18} className="text-orange-500" />}
                              {asset.type === 'link' && <Link weight="duotone" size={18} className="text-green-500" />}
                              {asset.type === 'text' && <FileText weight="duotone" size={18} className="text-purple-500" />}
                              <span className="text-xs bg-secondary px-2 py-0.5 rounded capitalize">{asset.category}</span>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={async () => {
                                if (!window.confirm('Delete this asset?')) return;
                                try {
                                  await axios.delete(`${API_URL}/api/admin/marketing/assets/${asset.id}`, { withCredentials: true });
                                  toast.success('Asset deleted');
                                  fetchAdminData();
                                } catch (error) {
                                  toast.error('Failed to delete');
                                }
                              }}
                            >
                              <Trash weight="bold" size={14} />
                            </Button>
                          </div>
                          <h4 className="font-medium text-sm">{asset.name}</h4>
                          {asset.description && <p className="text-xs text-muted-foreground mt-1">{asset.description}</p>}
                          {asset.url && (
                            <div className="mt-3 flex gap-2">
                              <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                                navigator.clipboard.writeText(asset.url);
                                toast.success('URL copied!');
                              }}>
                                <Copy weight="bold" size={12} className="mr-1" />
                                Copy
                              </Button>
                              {(asset.type === 'image' || asset.type === 'link') && (
                                <Button size="sm" variant="outline" onClick={() => window.open(asset.url, '_blank')}>
                                  <Link weight="bold" size={12} />
                                </Button>
                              )}
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-2">
                            Added {new Date(asset.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border rounded-lg bg-muted/20">
                      <Palette weight="duotone" size={48} className="mx-auto mb-4 text-muted-foreground/50" />
                      <p className="text-muted-foreground">No marketing assets yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Add graphics, campaigns, and brand resources</p>
                      <Button variant="outline" className="mt-4" onClick={() => setShowAssetDialog(true)}>
                        <Plus weight="bold" size={16} className="mr-2" />
                        Add First Asset
                      </Button>
                    </div>
                  )}
                </div>

                {/* Campaigns Section */}
                {campaigns.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Target weight="duotone" size={20} className="text-primary" />
                      Active Campaigns ({campaigns.length})
                    </h3>
                    <div className="space-y-3">
                      {campaigns.map((campaign) => (
                        <div key={campaign.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium">{campaign.name}</h4>
                              <p className="text-sm text-muted-foreground">{campaign.description}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                              {campaign.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>System Announcements</CardTitle>
                    <CardDescription>Create and manage site-wide announcements, maintenance windows, and alerts</CardDescription>
                  </div>
                  <Dialog open={showAnnouncementDialog} onOpenChange={(open) => {
                    setShowAnnouncementDialog(open);
                    if (!open) resetAnnouncementForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-announcement-btn">
                        <Plus weight="bold" size={18} className="mr-2" aria-hidden="true" />
                        New Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
                        <DialogDescription>
                          Create announcements for maintenance windows, updates, or important notices.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateAnnouncement} className="space-y-4 mt-4">
                        {/* Quick Templates */}
                        {!editingAnnouncement && (
                          <div className="border border-dashed border-border rounded-lg p-3 bg-muted/30">
                            <Label className="text-xs text-muted-foreground mb-2 block">Quick Templates</Label>
                            <div className="flex flex-wrap gap-2">
                              <Button type="button" variant="outline" size="sm" className="text-xs h-7"
                                data-testid="template-deployment-refresh"
                                onClick={() => {
                                  setAnnTitle('TurfPulse Update Deployed!');
                                  setAnnMessage(`We just pushed new features and fixes live! To make sure you're on the latest version, close TurfPulse and reopen it.\n\nStill seeing old content? Tap the "Having issues?" link at the top of your Dashboard for step-by-step refresh instructions for your device.`);
                                  setAnnType('info');
                                  setAnnDismissible(true);
                                  setAnnActive(true);
                                  toast.success('Template loaded — edit as needed');
                                }}>
                                Post-Deployment Refresh
                              </Button>
                              <Button type="button" variant="outline" size="sm" className="text-xs h-7"
                                data-testid="template-scheduled-maintenance"
                                onClick={() => {
                                  setAnnTitle('Scheduled Maintenance');
                                  setAnnMessage('TurfPulse will be briefly unavailable for scheduled maintenance. We expect this to take less than 30 minutes. Your data is safe — thank you for your patience!');
                                  setAnnType('maintenance');
                                  setAnnDismissible(true);
                                  setAnnActive(true);
                                  toast.success('Template loaded — edit as needed');
                                }}>
                                Scheduled Maintenance
                              </Button>
                              <Button type="button" variant="outline" size="sm" className="text-xs h-7"
                                data-testid="template-new-feature"
                                onClick={() => {
                                  setAnnTitle('New Feature Available!');
                                  setAnnMessage('We just added a new feature to TurfPulse. Check it out and let us know what you think!');
                                  setAnnType('success');
                                  setAnnDismissible(true);
                                  setAnnActive(true);
                                  toast.success('Template loaded — edit as needed');
                                }}>
                                New Feature
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <Label htmlFor="ann-title">Title (optional)</Label>
                            <Input
                              id="ann-title"
                              value={annTitle}
                              onChange={(e) => setAnnTitle(e.target.value)}
                              placeholder="e.g., Scheduled Maintenance"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="ann-message">Message *</Label>
                            <Textarea
                              id="ann-message"
                              value={annMessage}
                              onChange={(e) => setAnnMessage(e.target.value)}
                              placeholder="e.g., Sunday Dec 22, 2AM-4AM CST. Service may be briefly unavailable."
                              required
                              rows={annMessage.length > 200 ? 8 : 2}
                            />
                          </div>
                          <div>
                            <Label htmlFor="ann-type">Type</Label>
                            <Select value={annType} onValueChange={setAnnType}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="info">
                                  <div className="flex items-center gap-2">
                                    <Info weight="bold" size={16} className="text-primary" />
                                    Info
                                  </div>
                                </SelectItem>
                                <SelectItem value="warning">
                                  <div className="flex items-center gap-2">
                                    <Warning weight="bold" size={16} className="text-amber-500" />
                                    Warning
                                  </div>
                                </SelectItem>
                                <SelectItem value="maintenance">
                                  <div className="flex items-center gap-2">
                                    <Wrench weight="bold" size={16} className="text-blue-500" />
                                    Maintenance
                                  </div>
                                </SelectItem>
                                <SelectItem value="success">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle weight="bold" size={16} className="text-green-500" />
                                    Success
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Switch 
                                id="ann-dismissible" 
                                checked={annDismissible} 
                                onCheckedChange={setAnnDismissible}
                              />
                              <Label htmlFor="ann-dismissible" className="text-sm">Dismissible</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch 
                                id="ann-active" 
                                checked={annActive} 
                                onCheckedChange={setAnnActive}
                              />
                              <Label htmlFor="ann-active" className="text-sm">Active</Label>
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="ann-link-url">Link URL (optional)</Label>
                            <Input
                              id="ann-link-url"
                              type="url"
                              value={annLinkUrl}
                              onChange={(e) => setAnnLinkUrl(e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                          <div>
                            <Label htmlFor="ann-link-text">Link Text</Label>
                            <Input
                              id="ann-link-text"
                              value={annLinkText}
                              onChange={(e) => setAnnLinkText(e.target.value)}
                              placeholder="Learn more"
                            />
                          </div>
                          <div>
                            <Label htmlFor="ann-start">Schedule Start (optional)</Label>
                            <Input
                              id="ann-start"
                              type="datetime-local"
                              value={annScheduledStart}
                              onChange={(e) => setAnnScheduledStart(e.target.value)}
                            />
                          </div>
                          <div>
                            <Label htmlFor="ann-end">Schedule End (optional)</Label>
                            <Input
                              id="ann-end"
                              type="datetime-local"
                              value={annScheduledEnd}
                              onChange={(e) => setAnnScheduledEnd(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => {
                            setShowAnnouncementDialog(false);
                            resetAnnouncementForm();
                          }}>
                            Cancel
                          </Button>
                          <Button type="submit" data-testid="save-announcement-btn">
                            {editingAnnouncement ? 'Update' : 'Create'} Announcement
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {announcements.length > 0 ? (
                  <div className="space-y-3">
                    {announcements.map((ann) => (
                      <div 
                        key={ann.id} 
                        data-testid={`announcement-row-${ann.id}`}
                        className={`border rounded-lg p-4 ${ann.active ? 'border-border' : 'border-muted bg-muted/30 opacity-60'}`}
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getAnnouncementTypeIcon(ann.type)}
                              <span className="font-semibold">{ann.title || '(No title)'}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                ann.active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-muted text-muted-foreground'
                              }`}>
                                {ann.active ? 'Active' : 'Inactive'}
                              </span>
                              {ann.dismissible && (
                                <span className="text-xs px-2 py-0.5 rounded bg-secondary text-muted-foreground">
                                  Dismissible
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{ann.message}</p>
                            {(ann.scheduled_start || ann.scheduled_end) && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {ann.scheduled_start && `Starts: ${new Date(ann.scheduled_start).toLocaleString()}`}
                                {ann.scheduled_start && ann.scheduled_end && ' — '}
                                {ann.scheduled_end && `Ends: ${new Date(ann.scheduled_end).toLocaleString()}`}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleAnnouncementActive(ann)}
                              aria-label={ann.active ? 'Deactivate' : 'Activate'}
                            >
                              {ann.active ? <X weight="bold" size={16} /> : <Check weight="bold" size={16} />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditAnnouncement(ann)}
                              aria-label="Edit"
                            >
                              <PencilSimple weight="bold" size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeleteAnnouncement(ann.id)}
                              aria-label="Delete"
                            >
                              <Trash weight="bold" size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Megaphone weight="duotone" size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No announcements yet</p>
                    <p className="text-sm">Create your first announcement to notify users about maintenance, updates, or important news.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Partner Applications</CardTitle>
                    <CardDescription>Review and manage partner applications</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setShowAddPartner(true)} data-testid="admin-add-partner-btn">
                    <Plus weight="bold" size={16} className="mr-1" /> Add Partner
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {partners.length > 0 ? (
                  <div className="space-y-4">
                    {partners.map((partner) => (
                      <div key={partner.id} data-testid={`admin-partner-${partner.id}`} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold">{partner.business_name}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                partner.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                partner.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                              }`}>
                                {partner.status}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{partner.contact_name} &middot; {partner.email} &middot; {partner.phone}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              <strong>Service:</strong> {partner.service_type} &middot; <strong>Area:</strong> {partner.area_served}
                            </p>
                            <p className="text-sm mt-2">{partner.description}</p>
                            {partner.website && (
                              <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-block">
                                {partner.website}
                              </a>
                            )}
                          </div>
                          {partner.status === 'pending' && (
                            <div className="flex gap-2 ml-4 shrink-0">
                              <Button
                                data-testid={`approve-partner-${partner.id}`}
                                size="sm"
                                onClick={() => handlePartnerAction(partner.id, 'approve')}
                              >
                                <Check weight="bold" size={16} className="mr-1" /> Approve
                              </Button>
                              <Button
                                data-testid={`reject-partner-${partner.id}`}
                                size="sm"
                                variant="destructive"
                                onClick={() => handlePartnerAction(partner.id, 'reject')}
                              >
                                <X weight="bold" size={16} className="mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingPartner({ ...partner })}
                            data-testid={`edit-partner-${partner.id}`}
                          >
                            <PencilSimple weight="bold" size={14} className="mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeletePartner(partner)}
                            data-testid={`delete-partner-${partner.id}`}
                          >
                            <Trash weight="bold" size={14} className="mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No partner applications yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Affiliate Products Tab */}
          <TabsContent value="affiliate">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Affiliate Product Management</CardTitle>
                  <CardDescription>Add and manage affiliate product links (Amazon, etc.)</CardDescription>
                </div>
                <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-product-btn">
                      <Plus weight="bold" size={18} className="mr-2" />
                      Add Affiliate
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Affiliate Product</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateProduct} className="space-y-4">
                      <div>
                        <Label>Product Name</Label>
                        <Input
                          data-testid="product-name-input"
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={productDesc}
                          onChange={(e) => setProductDesc(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Affiliate Link</Label>
                        <Input
                          data-testid="affiliate-link-input"
                          type="url"
                          value={affiliateLink}
                          onChange={(e) => setAffiliateLink(e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Image URL (optional)</Label>
                        <Input
                          type="url"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Product Type</Label>
                        <select
                          value={productType}
                          onChange={(e) => setProductType(e.target.value)}
                          className="w-full border border-input rounded-md px-3 py-2"
                        >
                          <option value="PGR">PGR</option>
                          <option value="Fertilizer">Fertilizer</option>
                          <option value="Equipment">Equipment</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label>Mark Text (optional)</Label>
                        <Input
                          value={markText}
                          onChange={(e) => setMarkText(e.target.value)}
                          placeholder="e.g., Featured, Best Seller"
                        />
                      </div>
                      <Button type="submit" className="w-full">Create Product</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {products.map((product) => (
                    <div key={product.id} className="border border-border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                          {product.image_url && (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          )}
                          <div>
                            <h4 className="font-bold">{product.name}</h4>
                            <p className="text-sm text-muted-foreground">{product.description}</p>
                            <div className="flex gap-2 mt-2">
                              <span className="text-xs bg-secondary px-2 py-1 rounded">
                                {product.product_type}
                              </span>
                              {product.mark_text && (
                                <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded">
                                  {product.mark_text}
                                </span>
                              )}
                            </div>
                            <a
                              href={product.affiliate_link}
                              target="_blank"
                              rel="nofollow sponsored noopener noreferrer"
                              className="text-xs text-primary hover:underline mt-2 inline-block"
                            >
                              View Link →
                            </a>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash weight="bold" size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roadmap & Tasks Tab */}
          <TabsContent value="roadmap">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Product Roadmap</CardTitle>
                  <CardDescription>Priority tasks and upcoming features — P0 is highest priority</CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const priorityGroups = {};
                    roadmapTasks.forEach(t => {
                      if (!priorityGroups[t.priority]) priorityGroups[t.priority] = [];
                      priorityGroups[t.priority].push(t);
                    });
                    const priorityOrder = ['P0', 'P1', 'P2', 'P3', 'P4'];
                    const priorityColors = {
                      'P0': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
                      'P1': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800',
                      'P2': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
                      'P3': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
                      'P4': 'bg-slate-100 text-slate-700 dark:bg-slate-800/30 dark:text-slate-300 border-slate-200 dark:border-slate-700',
                    };
                    const statusStyles = {
                      done: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                      upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                      blocked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                      backlog: 'bg-slate-100 text-slate-600 dark:bg-slate-800/30 dark:text-slate-400',
                    };
                    const statusLabels = { done: 'Done', upcoming: 'Up Next', blocked: 'Blocked', backlog: 'Backlog' };

                    return (
                      <div className="space-y-6">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="border rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-600">{roadmapTasks.filter(t => t.status === 'done').length}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                          </div>
                          <div className="border rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-blue-600">{roadmapTasks.filter(t => t.status === 'upcoming').length}</p>
                            <p className="text-xs text-muted-foreground">Up Next</p>
                          </div>
                          <div className="border rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-red-600">{roadmapTasks.filter(t => t.status === 'blocked').length}</p>
                            <p className="text-xs text-muted-foreground">Blocked</p>
                          </div>
                          <div className="border rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-slate-500">{roadmapTasks.filter(t => t.status === 'backlog').length}</p>
                            <p className="text-xs text-muted-foreground">Backlog</p>
                          </div>
                        </div>

                        {/* Tasks by Priority */}
                        {priorityOrder.map(p => {
                          const tasks = priorityGroups[p];
                          if (!tasks) return null;
                          return (
                            <div key={p} data-testid={`roadmap-${p.toLowerCase()}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <span className={`text-xs font-bold px-2.5 py-1 rounded-md border ${priorityColors[p]}`}>{p}</span>
                                <span className="text-sm text-muted-foreground font-medium">
                                  {p === 'P0' ? 'Critical / In Progress' : p === 'P1' ? 'High Priority' : p === 'P2' ? 'Medium Priority' : p === 'P3' ? 'Low Priority' : 'Future / Nice to Have'}
                                </span>
                              </div>
                              <div className="space-y-2 ml-1">
                                {tasks.map(task => (
                                  <div key={task.id} className={`border rounded-lg p-3 flex items-start gap-3 ${task.status === 'done' ? 'opacity-60' : ''}`}>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`font-semibold text-sm ${task.status === 'done' ? 'line-through' : ''}`}>{task.title}</span>
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusStyles[task.status]}`}>
                                          {statusLabels[task.status]}
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
                                      {task.blocker && (
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">Blocker: {task.blocker}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Video Guides Tab */}
          <TabsContent value="videos">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle data-testid="admin-video-title">Video Guides Manager</CardTitle>
                    <CardDescription>Manage seasonal YouTube video rotations for Weed, Pest, PGR, and Disease pages. Videos auto-filter by current season.</CardDescription>
                  </div>
                  <Button data-testid="admin-add-video-btn" onClick={() => { resetVideoForm(); setShowVideoDialog(true); }}>
                    <Plus size={16} className="mr-1" /> Add Video
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 flex gap-2 flex-wrap">
                  {['weed', 'pest', 'pgr', 'disease', 'general'].map(cat => {
                    const count = videoGuides.filter(v => v.category === cat).length;
                    return (
                      <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-muted capitalize">
                        {cat} <span className="text-muted-foreground">({count})</span>
                      </span>
                    );
                  })}
                </div>

                {['weed', 'pest', 'pgr', 'disease', 'general'].map(cat => {
                  const catVideos = videoGuides.filter(v => v.category === cat);
                  if (catVideos.length === 0) return null;
                  return (
                    <div key={cat} className="mb-6">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 capitalize">{cat} Control Videos</h3>
                      <div className="space-y-2">
                        {catVideos.map(video => (
                          <div key={video.video_id} data-testid={`admin-video-row-${video.video_id}`} className={`flex items-center justify-between p-3 border rounded-lg ${video.is_active ? 'bg-background' : 'bg-muted/50 opacity-60'}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm truncate">{video.title}</span>
                                {!video.is_active && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Inactive</span>}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-muted-foreground">{video.creator}</span>
                                <span className="text-xs text-muted-foreground">|</span>
                                <span className="text-xs text-muted-foreground font-mono">{video.youtube_video_id}</span>
                                <span className="text-xs text-muted-foreground">|</span>
                                {video.seasons?.map(s => (
                                  <span key={s} className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 capitalize">{s}</span>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button variant="ghost" size="sm" data-testid={`admin-toggle-video-${video.video_id}`} onClick={() => handleToggleVideo(video.video_id)} title={video.is_active ? 'Deactivate' : 'Activate'}>
                                {video.is_active ? <Check size={14} className="text-green-600" /> : <X size={14} className="text-red-600" />}
                              </Button>
                              <Button variant="ghost" size="sm" data-testid={`admin-edit-video-${video.video_id}`} onClick={() => handleEditVideo(video)}>
                                <PencilSimple size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" data-testid={`admin-delete-video-${video.video_id}`} onClick={() => handleDeleteVideo(video.video_id)}>
                                <Trash size={14} className="text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {videoGuides.length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-8">No video guides yet. Click "Add Video" to get started.</p>
                )}
              </CardContent>
            </Card>

            {/* Add/Edit Video Dialog */}
            <Dialog open={showVideoDialog} onOpenChange={(open) => { if (!open) { resetVideoForm(); } setShowVideoDialog(open); }}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingVideo ? 'Edit Video Guide' : 'Add Video Guide'}</DialogTitle>
                  <DialogDescription>Add a YouTube video from a trusted lawn care creator.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Title *</Label>
                    <Input data-testid="video-form-title" value={videoForm.title} onChange={e => setVideoForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Pre-Emergent Timing Guide" />
                  </div>
                  <div>
                    <Label>YouTube Video ID *</Label>
                    <Input data-testid="video-form-youtube-id" value={videoForm.youtube_video_id} onChange={e => setVideoForm(prev => ({ ...prev, youtube_video_id: e.target.value }))} placeholder="dQw4w9WgXcQ" />
                    <p className="text-xs text-muted-foreground mt-1">The ID from the YouTube URL (e.g., youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong>)</p>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input data-testid="video-form-description" value={videoForm.description} onChange={e => setVideoForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of the video content" />
                  </div>
                  <div>
                    <Label>Creator *</Label>
                    <Select value={videoForm.creator} onValueChange={v => setVideoForm(prev => ({ ...prev, creator: v }))}>
                      <SelectTrigger data-testid="video-form-creator">
                        <SelectValue placeholder="Select creator" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ron Henry">Ron Henry</SelectItem>
                        <SelectItem value="GCI Matt">GCI Matt</SelectItem>
                        <SelectItem value="Lawn Insider">Lawn Insider</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <Select value={videoForm.category} onValueChange={v => setVideoForm(prev => ({ ...prev, category: v }))}>
                      <SelectTrigger data-testid="video-form-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weed">Weed Control</SelectItem>
                        <SelectItem value="pest">Pest Management</SelectItem>
                        <SelectItem value="pgr">PGR</SelectItem>
                        <SelectItem value="disease">Disease</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Seasons * (click to toggle)</Label>
                    <div className="flex gap-2 mt-1">
                      {['spring', 'summer', 'fall', 'winter', 'evergreen'].map(season => (
                        <button
                          key={season}
                          type="button"
                          data-testid={`video-form-season-${season}`}
                          onClick={() => toggleSeason(season)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${
                            videoForm.seasons.includes(season)
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-background text-muted-foreground border-border hover:border-green-400'
                          }`}
                        >
                          {season}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch data-testid="video-form-active" checked={videoForm.is_active} onCheckedChange={v => setVideoForm(prev => ({ ...prev, is_active: v }))} />
                    <Label>Active</Label>
                  </div>
                  <Button data-testid="video-form-save-btn" className="w-full" onClick={handleSaveVideo}>
                    {editingVideo ? 'Update Video' : 'Add Video'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Engagement Tab (privacy-safe page-view aggregates) */}
          <TabsContent value="engagement" data-testid="admin-engagement-tab-content">
            <EngagementCard />
          </TabsContent>

          {/* Business Tracker Tab */}
          <TabsContent value="tracker" data-testid="admin-tracker-tab-content">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>Business Tracker</CardTitle>
                    <CardDescription>Expenses, revenue, meetings, and assets — all timestamped</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={trackerFilter} onValueChange={setTrackerFilter}>
                      <SelectTrigger className="w-[140px]" data-testid="tracker-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="expense">Expenses</SelectItem>
                        <SelectItem value="revenue">Revenue</SelectItem>
                        <SelectItem value="meeting">Meetings</SelectItem>
                        <SelectItem value="asset">Assets</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="tracker-export-csv">
                      <DownloadSimple weight="bold" className="mr-1" size={14} /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="tracker-export-pdf">
                      <FileText weight="bold" className="mr-1" size={14} /> PDF
                    </Button>
                    <Button size="sm" onClick={() => { setEditingEntry(null); setTrackerForm({ entry_type: 'expense', date: new Date().toISOString().split('T')[0], description: '', category: '', amount: 0, receipt_info: '', notes: '' }); setCategorySuggestions([]); setShowTrackerDialog(true); }} data-testid="tracker-add-btn">
                      <Plus weight="bold" className="mr-1" size={16} /> Add Entry
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Running Totals */}
                {/* How-To Guide */}
                <Accordion type="single" collapsible className="mb-6">
                  <AccordionItem value="howto" className="border rounded-lg bg-muted/30">
                    <AccordionTrigger className="px-4 text-sm font-semibold">
                      <span className="flex items-center gap-2"><Info weight="duotone" size={16} className="text-blue-500" /> How to Use the Business Tracker</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="grid md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <p className="font-semibold text-foreground mb-1">Entry Types</p>
                          <ul className="space-y-1 list-disc pl-4">
                            <li><strong>Expense</strong> — Any money going out (software, legal fees, meals, office supplies). Always include amount + category.</li>
                            <li><strong>Revenue</strong> — Any money coming in (subscriptions, consulting, partnerships). Track the source in notes.</li>
                            <li><strong>Meeting</strong> — Business meetings, client calls, conferences. Log who attended and duration in notes. Log meal costs as a separate Expense entry.</li>
                            <li><strong>Asset</strong> — Equipment, hardware, software licenses (depreciable). Track purchase price as the amount.</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-semibold text-foreground mb-1">Best Practices</p>
                          <ul className="space-y-1 list-disc pl-4">
                            <li><strong>Receipt Info</strong> — Always log case #, order #, or confirmation #. Snap a photo and note where it's saved.</li>
                            <li><strong>Categories</strong> — Stay consistent: <code className="text-xs bg-muted px-1 rounded">Legal / IP</code>, <code className="text-xs bg-muted px-1 rounded">Operations</code>, <code className="text-xs bg-muted px-1 rounded">Meals & Entertainment</code>, <code className="text-xs bg-muted px-1 rounded">Office Supplies</code>, <code className="text-xs bg-muted px-1 rounded">Software</code>, <code className="text-xs bg-muted px-1 rounded">Hardware</code>, <code className="text-xs bg-muted px-1 rounded">Equipment</code>, <code className="text-xs bg-muted px-1 rounded">Travel / Mileage</code>, <code className="text-xs bg-muted px-1 rounded">Attire</code>, <code className="text-xs bg-muted px-1 rounded">Products</code>, <code className="text-xs bg-muted px-1 rounded">Services</code>, <code className="text-xs bg-muted px-1 rounded">Marketing Tools</code></li>
                            <li><strong>Auto-Suggest</strong> — As you type the description, category suggestions appear automatically. Click one to apply it.</li>
                          </ul>
                        </div>
                      </div>
                      {/* Tax Deductibility Guide */}
                      <div className="mt-4 pt-4 border-t">
                        <p className="font-semibold text-foreground mb-2">Tax Deductibility Guide — What Qualifies as a Business Expense</p>
                        <p className="text-xs text-muted-foreground mb-3">IRS rule: Must be <strong>"ordinary and necessary"</strong> for running the business. If you bought it or did it because of TurfPulse, log it.</p>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-green-600 mb-1">100% Deductible</p>
                            <ul className="space-y-0.5 text-xs list-disc pl-4">
                              <li><strong>Software</strong> — Hosting, domain, Stripe fees, dev tools, Zoom</li>
                              <li><strong>Legal / IP</strong> — LLC, trademark, copyright, attorney fees</li>
                              <li><strong>Office Supplies</strong> — Ink, paper, notebooks, pens</li>
                              <li><strong>Hardware</strong> — Laptop, monitor, iPad, phone (business use)</li>
                              <li><strong>Equipment</strong> — Spreaders, sprayers, mowers for demos</li>
                              <li><strong>Products</strong> — Fertilizer, herbicide, seed for testing/demos</li>
                              <li><strong>Marketing</strong> — Ads, flyers, AppSumo, website costs</li>
                              <li><strong>Marketing Tools</strong> — Email platforms, CRM, social scheduling</li>
                              <li><strong>Travel</strong> — Flights, hotels, rental cars, parking, tolls</li>
                              <li><strong>Mileage</strong> — 67.5 cents/mile (track every trip!)</li>
                              <li><strong>Attire</strong> — Branded/logo items ONLY (plain clothes don't count)</li>
                              <li><strong>Services</strong> — Contractors, freelancers, lawn services for testing</li>
                              <li><strong>Home Office</strong> — % of rent, utilities, internet (by sq ft)</li>
                              <li><strong>Education</strong> — Courses, books, conferences for the business</li>
                              <li><strong>Phone / Internet</strong> — Business percentage of monthly bill</li>
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-amber-600 mb-1">50% Deductible</p>
                            <ul className="space-y-0.5 text-xs list-disc pl-4">
                              <li><strong>Meals & Entertainment</strong> — Business meals with clients or partners</li>
                              <li>Must document: <em>who attended, where, and business purpose</em></li>
                              <li>Example: $500 client dinner = $250 deductible</li>
                            </ul>
                            <p className="text-xs font-semibold text-red-600 mt-3 mb-1">NOT Deductible</p>
                            <ul className="space-y-0.5 text-xs list-disc pl-4">
                              <li>Personal clothing (even if worn to meetings)</li>
                              <li>Commuting to your regular office</li>
                              <li>Personal portion of phone/internet</li>
                              <li>Fines or penalties</li>
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-blue-600 mb-1">Business Trip Checklist</p>
                            <p className="text-xs mb-1">Every item on a business trip is deductible if the trip has a business purpose:</p>
                            <ul className="space-y-0.5 text-xs list-disc pl-4">
                              <li>Flights / gas / mileage</li>
                              <li>Hotel / lodging</li>
                              <li>Rental car / rideshare</li>
                              <li>Meals during the trip (50%)</li>
                              <li>Demo equipment & supplies</li>
                              <li>Branded attire</li>
                              <li>Parking & tolls</li>
                              <li>Wi-Fi & phone charges</li>
                            </ul>
                            <p className="text-xs mt-2 font-medium">Log each as a separate entry with the trip name in notes for easy grouping.</p>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 italic">When in doubt — log it. Let your CPA sort out the edge cases at tax time. Keep every receipt.</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                {/* Running Totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {(() => {
                    const expenses = trackerEntries.filter(e => e.entry_type === 'expense').reduce((s, e) => s + (e.amount || 0), 0);
                    const revenue = trackerEntries.filter(e => e.entry_type === 'revenue').reduce((s, e) => s + (e.amount || 0), 0);
                    const meetings = trackerEntries.filter(e => e.entry_type === 'meeting').length;
                    const assets = trackerEntries.filter(e => e.entry_type === 'asset').reduce((s, e) => s + (e.amount || 0), 0);
                    return (
                      <>
                        <div className="border rounded-lg p-3 bg-red-50 dark:bg-red-950/30">
                          <p className="text-xs text-muted-foreground">Total Expenses</p>
                          <p className="text-lg font-bold text-red-600" data-testid="tracker-total-expenses">${expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-950/30">
                          <p className="text-xs text-muted-foreground">Total Revenue</p>
                          <p className="text-lg font-bold text-green-600" data-testid="tracker-total-revenue">${revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div className="border rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30">
                          <p className="text-xs text-muted-foreground">Meetings</p>
                          <p className="text-lg font-bold text-blue-600" data-testid="tracker-total-meetings">{meetings}</p>
                        </div>
                        <div className="border rounded-lg p-3 bg-purple-50 dark:bg-purple-950/30">
                          <p className="text-xs text-muted-foreground">Assets Value</p>
                          <p className="text-lg font-bold text-purple-600" data-testid="tracker-total-assets">${assets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                {/* Net P&L */}
                {(() => {
                  const expenses = trackerEntries.filter(e => e.entry_type === 'expense').reduce((s, e) => s + (e.amount || 0), 0);
                  const revenue = trackerEntries.filter(e => e.entry_type === 'revenue').reduce((s, e) => s + (e.amount || 0), 0);
                  const net = revenue - expenses;
                  return (
                    <div className={`border rounded-lg p-3 mb-6 ${net >= 0 ? 'bg-green-50 dark:bg-green-950/30 border-green-200' : 'bg-red-50 dark:bg-red-950/30 border-red-200'}`}>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium">Net P&L (YTD)</p>
                        <p className={`text-xl font-bold ${net >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="tracker-net-pl">
                          {net >= 0 ? '+' : ''}{net.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Financial Overview Charts */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* Monthly Expense/Revenue Trend */}
                  <div className="border rounded-lg p-4" data-testid="tracker-trend-chart">
                    <p className="text-sm font-semibold mb-3">Monthly Trend</p>
                    {(() => {
                      const monthlyData = {};
                      trackerEntries.forEach(e => {
                        const month = e.date?.substring(0, 7);
                        if (!month) return;
                        if (!monthlyData[month]) monthlyData[month] = { month, expenses: 0, revenue: 0 };
                        if (e.entry_type === 'expense') monthlyData[month].expenses += (e.amount || 0);
                        if (e.entry_type === 'revenue') monthlyData[month].revenue += (e.amount || 0);
                      });
                      const chartData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)).map(d => ({
                        ...d,
                        label: new Date(d.month + '-15').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                        net: d.revenue - d.expenses
                      }));
                      if (chartData.length === 0) return <p className="text-xs text-muted-foreground text-center py-8">No data yet</p>;
                      return (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                            <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, '']} />
                            <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="revenue" fill="#22c55e" name="Revenue" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </div>

                  {/* Expense Breakdown by Category */}
                  <div className="border rounded-lg p-4" data-testid="tracker-category-chart">
                    <p className="text-sm font-semibold mb-3">Expense Breakdown</p>
                    {(() => {
                      const categoryData = {};
                      trackerEntries.filter(e => e.entry_type === 'expense').forEach(e => {
                        const cat = e.category || 'Uncategorized';
                        categoryData[cat] = (categoryData[cat] || 0) + (e.amount || 0);
                      });
                      const COLORS = ['#2d5016', '#ef4444', '#3b82f6', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'];
                      const chartData = Object.entries(categoryData).map(([name, value], i) => ({
                        name, value, fill: COLORS[i % COLORS.length]
                      })).sort((a, b) => b.value - a.value);
                      if (chartData.length === 0) return <p className="text-xs text-muted-foreground text-center py-8">No expenses yet</p>;
                      return (
                        <div className="flex items-center gap-4">
                          <ResponsiveContainer width="50%" height={200}>
                            <PieChart>
                              <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                                {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                              </Pie>
                              <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, '']} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-1 flex-1">
                            {chartData.map((d, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: d.fill }} />
                                <span className="truncate">{d.name}</span>
                                <span className="ml-auto font-medium">${d.value.toFixed(0)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Cumulative P&L Chart */}
                <div className="border rounded-lg p-4 mb-6" data-testid="tracker-cumulative-chart">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold">Cumulative P&L Over Time</p>
                    <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="tracker-print-btn">
                      Print Report
                    </Button>
                  </div>
                  {(() => {
                    const sorted = [...trackerEntries].filter(e => e.entry_type === 'expense' || e.entry_type === 'revenue').sort((a, b) => a.date.localeCompare(b.date));
                    let cumExpense = 0, cumRevenue = 0;
                    const chartData = sorted.map(e => {
                      if (e.entry_type === 'expense') cumExpense += (e.amount || 0);
                      if (e.entry_type === 'revenue') cumRevenue += (e.amount || 0);
                      return {
                        date: new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        expenses: cumExpense,
                        revenue: cumRevenue,
                        net: cumRevenue - cumExpense
                      };
                    });
                    if (chartData.length === 0) return <p className="text-xs text-muted-foreground text-center py-8">No financial data yet</p>;
                    return (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                          <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, '']} />
                          <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="#22c55e" fillOpacity={0.15} name="Revenue" />
                          <Area type="monotone" dataKey="expenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.15} name="Expenses" />
                        </AreaChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>
                {/* Entries List */}
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {trackerEntries
                    .filter(e => trackerFilter === 'all' || e.entry_type === trackerFilter)
                    .map((entry) => (
                    <div key={entry.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-4 gap-y-1 hover:bg-muted/50 transition-colors" data-testid={`tracker-entry-${entry.id}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${
                          entry.entry_type === 'expense' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                          entry.entry_type === 'revenue' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                          entry.entry_type === 'meeting' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                          'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                        }`}>{entry.entry_type}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">{entry.date}</span>
                        <span className="text-sm font-medium truncate">{entry.description}</span>
                      </div>
                      <div className="flex items-center gap-3 w-full sm:w-auto sm:ml-4 justify-between sm:justify-end flex-shrink-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {entry.category && <span className="text-[10px] bg-muted px-2.5 py-0.5 rounded truncate max-w-[160px]">{entry.category}</span>}
                          {entry.amount > 0 && <span className="text-sm font-bold whitespace-nowrap">${entry.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                          setEditingEntry(entry);
                          setTrackerForm({ entry_type: entry.entry_type, date: entry.date, description: entry.description, category: entry.category || '', amount: entry.amount || 0, receipt_info: entry.receipt_info || '', notes: entry.notes || '' });
                          setShowTrackerDialog(true);
                        }} data-testid={`tracker-edit-${entry.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteTrackerEntry(entry.id)} data-testid={`tracker-delete-${entry.id}`}>
                          <Trash size={14} />
                        </Button>
                        </div>
                      </div>
                      {(entry.receipt_info || entry.notes) && (
                        <div className="w-full text-xs text-muted-foreground mt-1">
                          {entry.receipt_info && <span className="mr-3">Receipt: {entry.receipt_info}</span>}
                          {entry.notes && <span>Notes: {entry.notes}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                  {trackerEntries.filter(e => trackerFilter === 'all' || e.entry_type === trackerFilter).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No entries yet. Click "Add Entry" to start tracking.</p>
                  )}
                </div>
                {/* Timestamp footer */}
                <div className="mt-4 pt-3 border-t text-xs text-muted-foreground flex justify-between">
                  <span>{trackerEntries.length} total entries</span>
                  <span>Last updated: {trackerEntries[0]?.updated_at ? new Date(trackerEntries[0].updated_at).toLocaleString() : 'N/A'}</span>
                </div>

                {/* Calendar Planner */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-base font-semibold mb-4">Planner</h3>
                  <div className="grid md:grid-cols-[auto_1fr] gap-6">
                    <div className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={selectedPlannerDate}
                        onSelect={(date) => setSelectedPlannerDate(date)}
                        modifiers={{
                          hasEntry: trackerEntries.map(e => new Date(e.date + 'T12:00:00')),
                        }}
                        modifiersStyles={{
                          hasEntry: { fontWeight: 'bold', textDecoration: 'underline', color: 'var(--primary)' }
                        }}
                        className="rounded-md border"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold">
                          {selectedPlannerDate
                            ? selectedPlannerDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                            : 'Select a date'}
                        </h4>
                        {selectedPlannerDate && (
                          <Button size="sm" variant="outline" onClick={() => {
                            setEditingEntry(null);
                            setTrackerForm({
                              entry_type: 'meeting', date: selectedPlannerDate.toISOString().split('T')[0],
                              description: '', category: '', amount: 0, receipt_info: '', notes: ''
                            });
                            setCategorySuggestions([]);
                            setShowTrackerDialog(true);
                          }} data-testid="planner-add-btn">
                            <Plus weight="bold" size={14} className="mr-1" /> Add to this date
                          </Button>
                        )}
                      </div>
                      {selectedPlannerDate ? (
                        <div className="space-y-2 max-h-[220px] overflow-y-auto">
                          {trackerEntries.filter(e => e.date === selectedPlannerDate.toISOString().split('T')[0]).length > 0 ? (
                            trackerEntries.filter(e => e.date === selectedPlannerDate.toISOString().split('T')[0]).map(entry => (
                              <div key={entry.id} className="border rounded-lg p-3 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                    entry.entry_type === 'expense' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                                    entry.entry_type === 'revenue' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                    entry.entry_type === 'meeting' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                                    'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                                  }`}>{entry.entry_type}</span>
                                  <span className="font-medium">{entry.description}</span>
                                  {entry.amount > 0 && <span className="ml-auto font-bold">${entry.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>}
                                </div>
                                {entry.notes && <p className="text-xs text-muted-foreground mt-1">{entry.notes}</p>}
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground py-4 text-center">No entries on this date</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground mb-2">Upcoming</p>
                          {trackerEntries
                            .filter(e => new Date(e.date) >= new Date(new Date().toISOString().split('T')[0]))
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .slice(0, 5)
                            .map(entry => (
                              <div key={entry.id} className="border rounded-lg p-2 text-sm flex items-center gap-2 cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPlannerDate(new Date(entry.date + 'T12:00:00'))}>
                                <span className="text-xs text-muted-foreground w-20 flex-shrink-0">{new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                                  entry.entry_type === 'meeting' ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground'
                                }`}>{entry.entry_type}</span>
                                <span className="truncate">{entry.description}</span>
                              </div>
                          ))}
                          {trackerEntries.filter(e => new Date(e.date) >= new Date(new Date().toISOString().split('T')[0])).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">No upcoming events. Click a date to plan.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Add/Edit Entry Dialog */}
            <Dialog open={showTrackerDialog} onOpenChange={setShowTrackerDialog}>
              <DialogContent className="max-w-md" data-testid="tracker-entry-dialog">
                <DialogHeader>
                  <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
                  <DialogDescription>Track expenses, revenue, meetings, and assets</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={trackerForm.entry_type} onValueChange={(v) => setTrackerForm({ ...trackerForm, entry_type: v })}>
                      <SelectTrigger data-testid="tracker-form-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="revenue">Revenue</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="asset">Asset</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {trackerForm.entry_type === 'expense' && 'Any business cost — legal, software, meals, supplies, travel.'}
                      {trackerForm.entry_type === 'revenue' && 'Income from subscriptions, consulting, or partnerships.'}
                      {trackerForm.entry_type === 'meeting' && 'Log who, when, and why. Meal costs go under Expense separately.'}
                      {trackerForm.entry_type === 'asset' && 'Equipment, hardware, or software licenses. Amount = purchase price.'}
                    </p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={trackerForm.date} onChange={(e) => setTrackerForm({ ...trackerForm, date: e.target.value })} data-testid="tracker-form-date" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={trackerForm.description} onChange={(e) => {
                      setTrackerForm({ ...trackerForm, description: e.target.value });
                      handleSuggestCategory(e.target.value);
                    }} placeholder={
                      trackerForm.entry_type === 'expense' ? 'e.g., USPTO Trademark Filing, Office desk, Zoom subscription' :
                      trackerForm.entry_type === 'revenue' ? 'e.g., Stripe subscription payment — John D.' :
                      trackerForm.entry_type === 'meeting' ? 'e.g., Client dinner with GTLL — partnership discussion' :
                      'e.g., MacBook Pro 14" for development'
                    } data-testid="tracker-form-description" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Category</Label>
                      <Select value={trackerForm.category} onValueChange={(v) => setTrackerForm({ ...trackerForm, category: v })}>
                        <SelectTrigger data-testid="tracker-form-category">
                          <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Legal / IP">Legal / IP</SelectItem>
                          <SelectItem value="Legal / Entity">Legal / Entity</SelectItem>
                          <SelectItem value="Operations">Operations</SelectItem>
                          <SelectItem value="Meals & Entertainment">Meals & Entertainment</SelectItem>
                          <SelectItem value="Office Supplies">Office Supplies</SelectItem>
                          <SelectItem value="Software">Software</SelectItem>
                          <SelectItem value="Hardware">Hardware</SelectItem>
                          <SelectItem value="Equipment">Equipment</SelectItem>
                          <SelectItem value="Travel / Mileage">Travel / Mileage</SelectItem>
                          <SelectItem value="Marketing / Advertising">Marketing / Advertising</SelectItem>
                          <SelectItem value="Marketing Tools">Marketing Tools</SelectItem>
                          <SelectItem value="Attire">Attire</SelectItem>
                          <SelectItem value="Products">Products</SelectItem>
                          <SelectItem value="Services">Services</SelectItem>
                          <SelectItem value="Business Development">Business Development</SelectItem>
                          <SelectItem value="Payroll / Contractors">Payroll / Contractors</SelectItem>
                        </SelectContent>
                      </Select>
                      {categorySuggestions.length > 0 && !trackerForm.category && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="text-[10px] text-muted-foreground">Suggested:</span>
                          {categorySuggestions.map((s) => (
                            <button key={s} type="button" onClick={() => { setTrackerForm(prev => ({ ...prev, category: s })); setCategorySuggestions([]); }}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 hover:bg-green-200 transition-colors cursor-pointer">
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>{trackerForm.entry_type === 'asset' ? 'Purchase Price ($)' : 'Amount ($)'}</Label>
                      <Input type="number" step="0.01" value={trackerForm.amount} onChange={(e) => setTrackerForm({ ...trackerForm, amount: parseFloat(e.target.value) || 0 })} data-testid="tracker-form-amount" />
                    </div>
                  </div>
                  <div>
                    <Label>Receipt / Reference Info</Label>
                    <Input value={trackerForm.receipt_info} onChange={(e) => setTrackerForm({ ...trackerForm, receipt_info: e.target.value })} placeholder="Order #, case #, invoice #, or where receipt photo is saved" data-testid="tracker-form-receipt" />
                    <p className="text-[11px] text-muted-foreground mt-1">IRS requires receipts for expenses over $75. Always log a reference.</p>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea value={trackerForm.notes} onChange={(e) => setTrackerForm({ ...trackerForm, notes: e.target.value })} placeholder={
                      trackerForm.entry_type === 'expense' ? 'Business purpose, who was involved, deductibility notes...' :
                      trackerForm.entry_type === 'revenue' ? 'Payment source, plan tier, recurring or one-time...' :
                      trackerForm.entry_type === 'meeting' ? 'Attendees, start/end time, key outcomes, follow-up actions...' :
                      'Serial #, warranty info, expected useful life for depreciation...'
                    } rows={2} data-testid="tracker-form-notes" />
                  </div>
                  <Button className="w-full" onClick={handleSaveTrackerEntry} data-testid="tracker-form-save">
                    {editingEntry ? 'Update Entry' : 'Add Entry'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Price Audit Section */}
            <Card className="mt-6" data-testid="price-audit-section">
              <CardHeader className="cursor-pointer" onClick={() => { setPriceAuditOpen(!priceAuditOpen); if (!priceAudit) fetchLatestAudit(); }}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowsClockwise weight="bold" size={18} className="text-amber-500" />
                      Price Audit
                    </CardTitle>
                    <CardDescription>Monthly product price variance check (10%+ flagged)</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {priceAudit?.timestamp && (
                      <span className="text-xs text-muted-foreground">
                        Last: {new Date(priceAudit.timestamp).toLocaleDateString()}
                      </span>
                    )}
                    <svg className={`w-4 h-4 transition-transform ${priceAuditOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
              </CardHeader>
              {priceAuditOpen && (
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <Button size="sm" onClick={handleRunAudit} disabled={priceAuditLoading} data-testid="run-price-audit-btn">
                      <ArrowsClockwise weight="bold" size={14} className={`mr-1 ${priceAuditLoading ? 'animate-spin' : ''}`} />
                      {priceAuditLoading ? 'Running...' : 'Run Audit Now'}
                    </Button>
                    {priceAudit && (
                      <div className="flex gap-4 text-sm">
                        <span className="text-muted-foreground">{priceAudit.total_products} products tracked</span>
                        <span className={priceAudit.flagged_count > 0 ? 'text-amber-600 font-semibold' : 'text-green-600'}>
                          {priceAudit.flagged_count > 0 ? `${priceAudit.flagged_count} flagged` : 'All clear'}
                        </span>
                        {priceAudit.trigger && (
                          <span className="text-xs text-muted-foreground/60 italic">
                            ({priceAudit.trigger === 'scheduled' ? 'auto' : 'manual'})
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {priceAudit?.flagged?.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50 text-left">
                            <th className="px-3 py-2 font-medium">Product</th>
                            <th className="px-3 py-2 font-medium">Dept</th>
                            <th className="px-3 py-2 font-medium text-right">Previous</th>
                            <th className="px-3 py-2 font-medium text-right">Current</th>
                            <th className="px-3 py-2 font-medium text-right">Change</th>
                            <th className="px-3 py-2 font-medium text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {priceAudit.flagged.map((f, i) => (
                            <tr key={i} className="border-t border-border/50" data-testid={`audit-row-${f.product_id}`}>
                              <td className="px-3 py-2">
                                <span className="font-medium">{f.name}</span>
                                {f.unit_size && <span className="text-xs text-muted-foreground ml-1">/ {f.unit_size}</span>}
                                {f.has_affiliate && <span className="text-[10px] text-blue-500 ml-1">(affiliate)</span>}
                              </td>
                              <td className="px-3 py-2 capitalize text-muted-foreground">{f.category}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground">${f.old_price.toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-medium">${f.new_price.toFixed(2)}</td>
                              <td className={`px-3 py-2 text-right font-semibold ${f.direction === 'up' ? 'text-red-500' : 'text-green-500'}`}>
                                {f.direction === 'up' ? '+' : '-'}{f.pct_change}%
                              </td>
                              <td className="px-3 py-2 text-right">
                                {editingPrice === f.product_id ? (
                                  <div className="flex items-center gap-1 justify-end">
                                    <span className="text-xs">$</span>
                                    <input type="number" step="0.01" className="w-20 px-2 py-1 text-sm border rounded bg-background" value={newPriceValue} onChange={(e) => setNewPriceValue(e.target.value)} data-testid={`audit-price-input-${f.product_id}`} />
                                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleUpdatePrice(f.product_id, f.category)}>
                                      <Check weight="bold" size={14} />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setEditingPrice(null); setNewPriceValue(''); }}>
                                      <X weight="bold" size={14} />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingPrice(f.product_id); setNewPriceValue(f.new_price.toFixed(2)); }} data-testid={`audit-edit-${f.product_id}`}>
                                    Update
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : priceAudit ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <Check weight="bold" size={32} className="mx-auto mb-2 text-green-500" />
                      <p className="font-medium">No price variances detected</p>
                      <p className="text-xs mt-1">All products within 10% of last audit. Auto-checks run monthly with email alerts.</p>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <p>Run your first audit to establish a price baseline.</p>
                      <p className="text-xs mt-1">After that, monthly auto-checks will flag any 10%+ changes and email you.</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Code Health — static code audit (dead code, dupes, drift) */}
            <CodeHealthCard />

            {/* Environment migration — export/import admin data between preview/prod */}
            <DataMigrationCard />
          </TabsContent>
          <TabsContent value="calibration" data-testid="admin-calibration-tab-content">
            <CalibrationCard />
          </TabsContent>
          <TabsContent value="nfc" data-testid="admin-nfc-tab-content">
            <NfcRedirectCard />
          </TabsContent>
          <TabsContent value="manual">
            {(() => {
              const manualSections = [
                {
                  id: 'investor',
                  title: 'Investor & CPA Documents',
                  keywords: 'valuation revenue ARR pitch deck business plan financial projections market analysis TAM seed funding investor CPA stakeholder',
                  content: (
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="border rounded-lg p-4 bg-background">
                          <h4 className="font-bold mb-2">Operations Manual (OM)</h4>
                          <p className="text-sm text-muted-foreground mb-3">Complete business documentation including valuation analysis, financial projections, market analysis, and risk factors.</p>
                          <p className="text-xs text-muted-foreground mb-2"><strong>Location:</strong> /app/memory/OPERATIONS_MANUAL.md</p>
                          <p className="text-xs text-muted-foreground"><strong>Sections:</strong> Executive Summary, Product & Tech, Market Analysis, Business Model, Financial Projections, Valuation, Milestones, Use of Funds, Risk Factors, Legal & Compliance</p>
                        </div>
                        <div className="border rounded-lg p-4 bg-background">
                          <h4 className="font-bold mb-2">Business Plan (Pitch Deck)</h4>
                          <p className="text-sm text-muted-foreground mb-3">Shark Tank-ready investor pitch with market opportunity, competitive analysis, and 5-year projections.</p>
                          <p className="text-xs text-muted-foreground mb-2"><strong>Location:</strong> /app/memory/BUSINESS_PLAN.md</p>
                          <p className="text-xs text-muted-foreground"><strong>Highlights:</strong> $99B TAM, 5-8x LTV:CAC ratio, $500K seed ask, 18-month runway</p>
                        </div>
                      </div>
                      <div className="p-4 bg-muted rounded-lg">
                        <h4 className="font-semibold mb-2">Current Valuation Summary</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div><p className="text-2xl font-bold text-primary">$75K-$120K</p><p className="text-xs text-muted-foreground">Pre-Revenue Value</p></div>
                          <div><p className="text-2xl font-bold">$65K-$95K</p><p className="text-xs text-muted-foreground">Build Cost</p></div>
                          <div><p className="text-2xl font-bold">5-8x</p><p className="text-xs text-muted-foreground">Target LTV:CAC</p></div>
                          <div><p className="text-2xl font-bold">$15M</p><p className="text-xs text-muted-foreground">Year 5 ARR Target</p></div>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'how-it-works',
                  title: 'How TurfPulse Works',
                  keywords: 'GDD growing degree days business model revenue freemium gate the output pricing tier free homeowner enthusiast subscription Open-Meteo OpenWeather weather temperature formula calculation T_base',
                  content: (
                    <div className="space-y-4">
                      <div className="bg-muted/50 rounded-lg p-4 border">
                        <h4 className="font-bold mb-2">The Core Idea</h4>
                        <p className="text-sm">TurfPulse tracks <strong>Growing Degree Days (GDD)</strong> — a heat-accumulation metric — so homeowners know <em>exactly</em> when to apply PGRs, pre-emergents, and other turf treatments based on science, not guessing. The app pulls actual historical daily high/low temps from Open-Meteo for each user's location and accumulates GDD from Jan 1.</p>
                      </div>
                      <div>
                        <h4 className="font-bold mb-2">GDD Calculation</h4>
                        <code className="block bg-muted p-2 rounded mb-2 text-sm">GDD = ((T_max + T_min) / 2) - T_base</code>
                        <ul className="text-sm space-y-1 list-disc pl-5">
                          <li><strong>T_base = 50°F</strong> for both cool-season and warm-season grasses</li>
                          <li>If the result is negative, that day contributes 0 GDD</li>
                          <li>Standard reapplication window: <strong>200-300 GDD</strong> between applications</li>
                          <li>Weather data source: <strong>Open-Meteo</strong> (free, no API key). Current conditions from <strong>OpenWeather</strong> (API key required)</li>
                        </ul>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <h4 className="font-bold mb-2">Revenue Model: "Gate the Output"</h4>
                        <p className="text-sm mb-2">TurfPulse uses a freemium model that <strong>never</strong> gates GDD tracking itself. We gate the <em>outputs</em>:</p>
                        <ul className="text-sm space-y-1 list-disc pl-5">
                          <li><strong>Free:</strong> Full GDD tracking, daily breakdown, weather, all departments, calendar, partners, irrigation guides. Limited to 1 property, 1 zone, last 5 spray log entries. No CSV/PDF export.</li>
                          <li><strong>Homeowner Seasonal ($50/season):</strong> Unlimited GDD tracking, full spray history, PGR & Weed Control, equipment log, spreader settings, seasonal calendar, email alerts, CSV export. 1 month free trial included.</li>
                          <li><strong>Enthusiast Annual ($80/year):</strong> Everything in Homeowner plus Pest & Disease departments, soil analysis, 5-day spray forecast, unlimited zones, priority support, PDF reports, PWS integration. 2 months free trial. Just $6.66/month.</li>
                        </ul>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <h4 className="font-bold mb-1">Critical Rule</h4>
                        <p className="text-sm">Do <strong>NOT</strong> implement time-based trials (30-day free trial, etc.). The freemium model depends on users seeing value forever so they convert voluntarily.</p>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'tab-guide',
                  title: 'Admin Panel — Tab-by-Tab Guide',
                  keywords: 'products users analytics irrigation announce announcements marketing partners affiliate tab CSV import EPA add product edit delete search sort tier upgrade downgrade comp UTM campaign Rachio disconnect brand asset logo',
                  content: (
                    <div className="space-y-4 text-sm">
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Products Tab</h4>
                        <p className="text-muted-foreground mb-2">Master list of every treatment product. 4 categories: PGR, Weed, Pest, Disease.</p>
                        <ul className="list-disc pl-5 space-y-1"><li><strong>Add Product:</strong> Click "+ Add Product" — name, active ingredient, manufacturer, EPA number, GDD interval, reapplication days, cost.</li><li><strong>Edit/Delete:</strong> Pencil icon to edit, trash to delete. Changes are immediate.</li><li><strong>Import CSV:</strong> Columns: name, active_ingredient, manufacturer, epa_number, gdd_interval, reapplication_days, cost, description.</li><li><strong>EPA Numbers:</strong> Always include. Format: XXX-XXX or XXX-XXX-XXXXX.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Users Tab</h4>
                        <p className="text-muted-foreground mb-2">View and manage every registered user. Search by name/email, sort by newest, tier, or alphabetical.</p>
                        <ul className="list-disc pl-5 space-y-1"><li><strong>Set Tier:</strong> Assign Free, Homeowner Seasonal, or Enthusiast Annual. Optional expiration date.</li><li><strong>Edit Profile:</strong> Change grass type (cool/warm-season) or role (user/admin).</li><li><strong>Comp an account:</strong> Set tier to Enthusiast Annual, no expiration = lifetime comp.</li><li><strong>Premium badge:</strong> Crown icon next to premium users.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Analytics Tab</h4>
                        <p className="text-muted-foreground mb-2">Campaign tracking via UTM parameters.</p>
                        <ul className="list-disc pl-5 space-y-1"><li><strong>UTM format:</strong> <code>?utm_source=facebook&utm_medium=social&utm_campaign=teaser1</code></li><li>Tracks signups per campaign, premium conversions, conversion rate, last signup date.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Irrigation Tab</h4>
                        <p className="text-muted-foreground mb-2">Monitor smart irrigation integrations (Rachio). Shows connected users, devices, last sync.</p>
                        <ul className="list-disc pl-5 space-y-1"><li><strong>Disconnect:</strong> Remove a user's irrigation connection if needed.</li><li>Users self-connect from their Irrigation dashboard page.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Announce Tab</h4>
                        <p className="text-muted-foreground mb-2">Site-wide banners on user dashboards.</p>
                        <ul className="list-disc pl-5 space-y-1"><li><strong>Types:</strong> Info, Warning, Maintenance, Success.</li><li><strong>Scheduling:</strong> Set start/end dates for auto show/hide.</li><li><strong>Dismissible vs. Persistent:</strong> Persistent for critical outages.</li><li><strong>Best practice:</strong> Post Maintenance 24-48 hours before downtime.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Marketing Tab</h4>
                        <p className="text-muted-foreground mb-2">Brand asset library and campaign management.</p>
                        <ul className="list-disc pl-5 space-y-1"><li>Add assets: images, documents, links, text. Categorize as graphic, campaign, email, social, logo, merch.</li><li>Primary logo and brand colors pinned at top.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Partners Tab</h4>
                        <p className="text-muted-foreground mb-2">Manage partner businesses in the user-facing directory.</p>
                        <ul className="list-disc pl-5 space-y-1"><li>Approve/Reject pending applications.</li><li>Current: Georgetown Lawn Leveling, Reel-Pros, Central Texas Lawn Leveling, Horizon Distributors.</li><li>Vet before approving: real business, website, lawn-related services.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Affiliate Tab</h4>
                        <p className="text-muted-foreground mb-2">Affiliate product links (Amazon, etc.) shown as recommendations.</p>
                        <ul className="list-disc pl-5 space-y-1"><li>Add products with name, description, affiliate URL, price, category.</li><li>Revenue source via affiliate commissions.</li></ul>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'routines',
                  title: 'Routines & Cadence',
                  keywords: 'daily weekly monthly check review MRR conversion rate partner audit announcements seasonal product catalog signups',
                  content: (
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="border rounded-lg p-4">
                        <h4 className="font-bold text-green-700 dark:text-green-400 mb-2">Daily</h4>
                        <ul className="text-sm list-disc pl-5 space-y-1"><li>Glance at stats bar (Total Users, Premium, MRR)</li><li>Check for new partner applications</li><li>Scan for user support emails</li></ul>
                      </div>
                      <div className="border rounded-lg p-4">
                        <h4 className="font-bold text-blue-700 dark:text-blue-400 mb-2">Weekly</h4>
                        <ul className="text-sm list-disc pl-5 space-y-1"><li>Review Analytics — which campaigns convert?</li><li>Check active announcements — remove stale ones</li><li>Review new signups and grass type distribution</li><li>Spot-check product catalog accuracy</li></ul>
                      </div>
                      <div className="border rounded-lg p-4">
                        <h4 className="font-bold text-purple-700 dark:text-purple-400 mb-2">Monthly</h4>
                        <ul className="text-sm list-disc pl-5 space-y-1"><li>MRR trend — are we growing?</li><li>Premium conversion rate improving?</li><li>Update seasonal product recommendations</li><li>Refresh marketing assets for season</li><li>Audit partner directory — dead links?</li></ul>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'common-requests',
                  title: 'Common User Requests',
                  keywords: 'support help spray history GDD wrong refund free upgrade app not loading Rachio connect troubleshoot FAQ',
                  content: (
                    <div className="space-y-3 text-sm">
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">"I can't see my spray history"</h4><p className="text-muted-foreground">Free tier only shows last 5 entries. Check their tier in Users tab. Upgrade if they should have access, or explain Homeowner Seasonal unlocks full history.</p></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">"My GDD number looks wrong"</h4><p className="text-muted-foreground">GDD resets Jan 1. Verify their zip code is correct (wrong location = wrong temps). Data comes from Open-Meteo historical records. If they moved, they need to update their location.</p></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">"I want a refund"</h4><p className="text-muted-foreground">Stripe dashboard to issue refunds. Then downgrade tier to Free in Users tab. Be generous — it's $80/yr max and goodwill matters more.</p></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">"Can I get a free upgrade?"</h4><p className="text-muted-foreground">"Set Tier" in Users tab. Enthusiast Annual + no expiration = lifetime comp. Set expiration for temporary promos.</p></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">"The app isn't loading"</h4><p className="text-muted-foreground">Check if it's just them. If app is actually down, post Maintenance announcement immediately. Check backend logs and weather API status.</p></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">"How do I connect my Rachio?"</h4><p className="text-muted-foreground">Direct them to the Irrigation page in their dashboard. They need their Rachio API key from the Rachio app settings. Self-service.</p></div>
                    </div>
                  )
                },
                {
                  id: 'emergency',
                  title: 'Emergency Procedures',
                  keywords: 'app down outage weather data stopped Stripe payments failing database crash MongoDB hosting error emergency break fix',
                  content: (
                    <div className="space-y-3 text-sm">
                      <div className="border border-red-200 dark:border-red-800 rounded-lg p-3"><h4 className="font-bold mb-1">App is completely down</h4><ol className="list-decimal pl-5 space-y-1"><li>Check if it's hosting or code.</li><li>Hosting: contact hosting provider.</li><li>Code: check backend error logs. Most crashes = missing env vars or bad deploy.</li><li>Post Maintenance announcement with ETA.</li></ol></div>
                      <div className="border border-red-200 dark:border-red-800 rounded-lg p-3"><h4 className="font-bold mb-1">Weather data stops updating</h4><p className="text-muted-foreground">GDD uses Open-Meteo (very reliable). Current weather uses OpenWeather (API key in backend .env). If OpenWeather is down, dashboard weather fails but GDD still works. Check API key quota.</p></div>
                      <div className="border border-red-200 dark:border-red-800 rounded-lg p-3"><h4 className="font-bold mb-1">Stripe payments failing</h4><p className="text-muted-foreground">Check Stripe dashboard. Common causes: expired cards, insufficient funds (user-side), webhook misconfiguration (our side). Users keep free tier access while billing is resolved.</p></div>
                      <div className="border border-red-200 dark:border-red-800 rounded-lg p-3"><h4 className="font-bold mb-1">Database issues</h4><p className="text-muted-foreground">MongoDB connection in backend/.env (MONGO_URL). If DB goes down, entire app is affected. Contact hosting support immediately. Backups should be configured at hosting level.</p></div>
                    </div>
                  )
                },
                {
                  id: 'april-2026-features',
                  title: 'April 2026 Feature Release',
                  keywords: 'merch marketplace group buy smart plan AI diagnosis inspections photo vendor assets truck magnet QR code FIFRA stripe pro tier',
                  content: (
                    <div className="space-y-4 text-sm">
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                        <h4 className="font-bold mb-2">New Features Shipped — April 12, 2026</h4>
                        <p className="text-muted-foreground mb-2">Biggest single-session update in TurfPulse history.</p>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">AI Photo Diagnosis</h4>
                        <p className="text-muted-foreground mb-2">Users snap a photo of a lawn issue → GPT-4o-mini vision identifies weeds, disease, pests → provides treatment steps + prevention + timing.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Endpoint:</strong> POST /api/inspections/diagnose</li><li><strong>Access:</strong> Free tier: 3/month. Pro tier: unlimited.</li><li><strong>Auto-fills:</strong> Inspection form with AI diagnosis (type, severity, description)</li><li><strong>Uses:</strong> Emergent LLM Key (EMERGENT_LLM_KEY in backend .env)</li></ul>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Smart Lawn Plan (AI)</h4>
                        <p className="text-muted-foreground mb-2">Personalized 5-7 action plan generated from GDD, weather forecast, activity log, sprinkler data, and grass type.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Endpoints:</strong> GET /api/smart-plan (generate), GET /api/smart-plan/cached (read)</li><li><strong>Dashboard:</strong> SmartPlanStrip shows top 3 actions</li><li><strong>Full page:</strong> /my-plan with checkable action items + progress bar</li><li><strong>Rules:</strong> Never prescribes mowing height. Respects 2-day mow buffer. Weather-aware (no spray before rain).</li></ul>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Field Inspections</h4>
                        <p className="text-muted-foreground mb-2">Zone-linked observations with 16 issue types, 3 severity levels, and resolve workflow.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Page:</strong> /inspections</li><li><strong>Types:</strong> Fungus/Disease, Water Buildup, Dry/Brown Patches, Pest Damage, Weed Pressure, Bare Spots, Compaction, Thatch Buildup, Drainage Issue, Mowing Damage, Chemical Burn, Animal Damage, Shade Stress, Heat Stress, Cold Damage, Other</li><li><strong>Summary endpoint:</strong> GET /api/inspections/summary — open issues by zone with severe count</li></ul>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Merch Lab</h4>
                        <p className="text-muted-foreground mb-2">17 product concepts (16 visible) with AI-generated branded mockups. Community voting + Notify Me.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Page:</strong> /merch</li><li><strong>Products:</strong> Greenkeeper Polo, Know Your Number Tee, Trust the Data Tee, Pulse Trucker, Performance Cap, Turf Pack stickers, Turf Flag, Full Send Flag, GDD Koozie, Quarter-Zip, UV Hoodie, Lawn Wife Crewneck, EKG Patches, GDD Button-Down Jersey, Sublimation Golf (hidden), Cheat Sheet PDF, Wallpapers</li><li><strong>Vendor Assets:</strong> /vendor-assets — logos, color specs, placement guides, mockup downloads</li><li><strong>Single heartbeat rule:</strong> ALL EKG logos must show ONE spike. Double = medical red flag.</li></ul>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Community Marketplace (Group Buys)</h4>
                        <p className="text-muted-foreground mb-2">Bulletin board for splitting bulk lawn products. TurfPulse is NOT involved in transactions.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Page:</strong> /marketplace</li><li><strong>Terms:</strong> /marketplace-terms — full legal page with FIFRA compliance, indemnification</li><li><strong>FIFRA Compliance:</strong> Chemical categories (PGR, Pre-Emergent, Post-Emergent Herbicide, Fungicide, Insecticide) are BLOCKED. All 50 states regulate pesticide sales.</li><li><strong>Allowed:</strong> Fertilizer, Soil Amendment, Seed, Equipment, Sprayer Parts, Mower Parts, Other</li><li><strong>Listings:</strong> 30-day expiry with renew option. Waiver required to post or join.</li><li><strong>Notifications:</strong> Organizer notified on joins/comments. Participants notified on comments.</li></ul>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Stripe Tiers (To Wire)</h4>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Monthly Pro ($9.99):</strong> price_1TLS1b2abaQuFCwvvgWl11Id</li><li><strong>Seasonal ($50):</strong> price_1THupn2abaQuFCwvR3bqWmMg</li><li><strong>Annual ($79.99):</strong> price_1THuwA2abaQuFCwvk3rqNy6f</li></ul>
                      </div>

                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">Print Assets</h4>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Truck Magnet:</strong> /turfpulse-truck-banner-print.png (24"x12" @ 300dpi) — for VistaPrint</li><li><strong>QR Code:</strong> /turfpulse-merch-qr.png — branded pulse logo center, links to /merch</li><li><strong>NOTE:</strong> QR currently points to preview URL. Regenerate with production URL after deploy.</li></ul>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'legal',
                  title: 'Legal & Compliance — What to Watch For',
                  keywords: 'FIFRA EPA label law pesticide record-keeping licensed applicator FTC affiliate disclosure GDPR CCPA privacy data deletion ADA WCAG accessibility LLC trademark copyright terms service',
                  content: (
                    <div className="space-y-3 text-sm">
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <h4 className="font-bold mb-1">#1 — "The Label Is the Law" (FIFRA)</h4>
                        <p className="text-muted-foreground mb-2">FIFRA makes it a <strong>federal violation</strong> to use a pesticide inconsistent with its label. We recommend <em>timing</em> based on GDD but <strong>never</strong> override label directions.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Watch:</strong> Any copy readable as "apply this now" without disclaimer. Every recommendation must include: <em>"Always read and follow product label directions."</em></li><li><strong>Lives:</strong> Dashboard spray log form + EPA compliance footer.</li><li><strong>Risk:</strong> High. If a user misapplies and blames TurfPulse, we need the disclaimer trail.</li></ul>
                      </div>
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <h4 className="font-bold mb-1">#2 — EPA Record-Keeping</h4>
                        <p className="text-muted-foreground mb-2">We auto-capture weather data (temp, humidity, wind) at each application log for EPA compliance.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Watch:</strong> Weather auto-capture must work. If weather API is down, logged applications have a compliance gap.</li><li><strong>Lives:</strong> Application logs (temp_f, humidity, wind_mph fields). Season PDF includes EPA compliance rate.</li><li><strong>If asked by inspector:</strong> Export user's Season PDF — dates, products, weather, GDD at time of application.</li></ul>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <h4 className="font-bold mb-1">#3 — We Are Not Licensed Applicators</h4>
                        <p className="text-muted-foreground mb-2">We're a <strong>timing tool</strong>, not a licensed advisor. We don't recommend products — we track <em>when</em> to apply products users chose.</p>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Watch:</strong> Never say "You should apply X." Use "Your window for [product] is open."</li><li><strong>Tone:</strong> "Assistant" not "Expert."</li><li><strong>If asked "what product?":</strong> Direct to local extension office or licensed pro.</li></ul>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <h4 className="font-bold mb-1">#4 — Affiliate Disclosures (FTC)</h4>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Watch:</strong> Every page with affiliate links needs visible disclosure.</li><li><strong>Lives:</strong> /disclosure page + inline near affiliate links.</li><li><strong>Adding new links:</strong> Make sure disclosure is visible, not below the fold.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">#5 — Data Privacy (GDPR / CCPA)</h4>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Deletion requests:</strong> Must comply. Delete account + all logs, properties, zones.</li><li><strong>Email:</strong> Only marketing emails to opted-in users. Transactional emails don't require opt-in.</li><li><strong>Lives:</strong> /privacy, /terms.</li></ul>
                      </div>
                      <div className="border rounded-lg p-3">
                        <h4 className="font-bold mb-1">#6 — Accessibility (ADA / WCAG)</h4>
                        <ul className="list-disc pl-5 space-y-1 text-muted-foreground"><li><strong>Watch:</strong> New UI features must be keyboard-navigable, proper contrast, alt text on images.</li><li><strong>Lives:</strong> /accessibility.</li></ul>
                      </div>
                      <div className="bg-muted rounded-lg p-3 mt-2">
                        <h4 className="font-bold mb-2">Current Legal Status</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-muted-foreground"><div><strong>Texas LLC:</strong> Formed (Dec 2025)</div><div><strong>Trademark:</strong> TurfPulse™ filed USPTO</div><div><strong>Domain:</strong> getturfpulse.com</div><div><strong>Privacy Policy:</strong> /privacy</div><div><strong>Terms:</strong> /terms</div><div><strong>Disclosure:</strong> /disclosure</div></div>
                        <p className="text-xs text-muted-foreground mt-2"><strong>Pending:</strong> Copyright registration ($65), attorney review of Terms & Privacy.</p>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'access',
                  title: 'Access & Key Accounts',
                  keywords: 'admin portal login secret key password Stripe Resend OpenWeather Open-Meteo MongoDB credentials API key tone voice assistant persona',
                  content: (
                    <div className="space-y-3 text-sm">
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">Admin Portal Access</h4><ul className="list-disc pl-5 space-y-1"><li><strong>URL:</strong> /admin-portal</li><li><strong>Method 1 — Quick Login:</strong> Log in with admin-role email/password</li><li><strong>Method 2 — Secret Key:</strong> Enter admin secret key (ask owner if you don't have it)</li><li>Grant admin: Users tab → Edit → set Role to "Admin"</li></ul></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">External Services</h4><ul className="list-disc pl-5 space-y-1"><li><strong>Stripe</strong> — Payments. stripe.com. Keys in backend .env.</li><li><strong>Resend</strong> — Emails. resend.com. Key in backend .env.</li><li><strong>OpenWeather</strong> — Current weather. openweathermap.org. Key in backend .env.</li><li><strong>Open-Meteo</strong> — Historical weather (GDD). Free, no key.</li><li><strong>MongoDB</strong> — Database. MONGO_URL in backend .env.</li></ul></div>
                      <div className="border rounded-lg p-3"><h4 className="font-bold mb-1">Tone & Voice</h4><p className="text-muted-foreground">TurfPulse uses an <strong>"Assistant" persona</strong>, not "Expert." Friendly and approachable: "Hey, here's what I'm seeing" / "You can probably skip watering today" / "Looks like you're in the window." No clinical or condescending language.</p></div>
                    </div>
                  )
                },
                {
                  id: 'references',
                  title: 'Need More Info? — Reference Docs',
                  keywords: 'PRD product requirements roadmap changelog deploy checklist investor letter business intelligence documentation files reference',
                  content: (
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Product Requirements (PRD)</h4><p className="text-xs text-muted-foreground mb-1">Feature specs, user personas, gating rules, backlog.</p><code className="text-xs">/app/memory/PRD.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Operations Manual (Full)</h4><p className="text-xs text-muted-foreground mb-1">Valuation, financial projections, market analysis, risk factors.</p><code className="text-xs">/app/memory/OPERATIONS_MANUAL.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Business Plan / Pitch Deck</h4><p className="text-xs text-muted-foreground mb-1">TAM, competitive analysis, 5-year projections.</p><code className="text-xs">/app/memory/BUSINESS_PLAN.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Business Intelligence</h4><p className="text-xs text-muted-foreground mb-1">Competitor analysis, pricing benchmarks, growth strategy.</p><code className="text-xs">/app/memory/BUSINESS_INTELLIGENCE.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Roadmap</h4><p className="text-xs text-muted-foreground mb-1">Prioritized backlog — what's next and why.</p><code className="text-xs">/app/memory/ROADMAP.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Changelog</h4><p className="text-xs text-muted-foreground mb-1">Dated log of every change shipped.</p><code className="text-xs">/app/memory/CHANGELOG.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Deploy Checklist</h4><p className="text-xs text-muted-foreground mb-1">Step-by-step deployment process.</p><code className="text-xs">/app/memory/DEPLOY_CHECKLIST.md</code></div>
                        <div className="border rounded-lg p-3 bg-background"><h4 className="font-bold text-sm mb-1">Investor Letter</h4><p className="text-xs text-muted-foreground mb-1">Shareholder update template.</p><code className="text-xs">/app/memory/INVESTOR_LETTER.md</code></div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-sm"><h4 className="font-bold mb-1">Still stuck?</h4><p className="text-muted-foreground">Backend logic: <code>/app/backend/server.py</code> and <code>/app/backend/routes/</code>. Frontend pages: <code>/app/frontend/src/pages/</code>. When in doubt, reach out to the owner before making changes.</p></div>
                    </div>
                  )
                },
                {
                  id: 'glossary',
                  title: 'Glossary',
                  keywords: 'GDD growing degree days PGR plant growth regulator pre-emergent post-emergent FRAC fungicide active ingredient AI EPA registration reapplication interval cool-season warm-season bermuda fescue bluegrass ET evapotranspiration T_base base temperature definition terms',
                  content: (
                    <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                      <div><h4 className="font-bold text-sm">GDD (Growing Degree Days)</h4><p className="text-sm text-muted-foreground">A heat-accumulation metric measuring daily warmth above a base temperature (50°F). Used to predict treatment timing based on actual plant development rather than calendar dates.</p></div>
                      <div><h4 className="font-bold text-sm">PGR (Plant Growth Regulator)</h4><p className="text-sm text-muted-foreground">Chemical applied to suppress vertical growth, redirect energy to lateral spread, improve density. Active ingredients: Trinexapac-ethyl (Primo Maxx), Flurprimidol (Cutless).</p></div>
                      <div><h4 className="font-bold text-sm">Pre-Emergent</h4><p className="text-sm text-muted-foreground">Herbicide applied before weed seeds germinate. Creates chemical barrier in soil. Timing: soil temps reach 55°F or around 150-200 GDD.</p></div>
                      <div><h4 className="font-bold text-sm">Post-Emergent</h4><p className="text-sm text-muted-foreground">Herbicide applied after weeds have emerged. Can be selective (targets specific weeds) or non-selective (kills all vegetation).</p></div>
                      <div><h4 className="font-bold text-sm">FRAC (Fungicide Resistance Action Committee)</h4><p className="text-sm text-muted-foreground">Classifies fungicides by mode of action. FRAC codes help rotate products to prevent resistance.</p></div>
                      <div><h4 className="font-bold text-sm">AI (Active Ingredient)</h4><p className="text-sm text-muted-foreground">Chemical compound providing the intended effect. Products may share the same AI under different brands.</p></div>
                      <div><h4 className="font-bold text-sm">EPA Registration Number</h4><p className="text-sm text-muted-foreground">Unique ID from the Environmental Protection Agency for every registered pesticide. Required for legal sale/application. Format: XXX-XXX or XXX-XXX-XXXXX.</p></div>
                      <div><h4 className="font-bold text-sm">Reapplication Interval</h4><p className="text-sm text-muted-foreground">Minimum days or GDD between consecutive applications. Dictated by the product label (which is the law).</p></div>
                      <div><h4 className="font-bold text-sm">Cool-Season Grass</h4><p className="text-sm text-muted-foreground">Thrives 60-75°F. Kentucky Bluegrass, Perennial Ryegrass, Tall Fescue. Peak growth: spring and fall.</p></div>
                      <div><h4 className="font-bold text-sm">Warm-Season Grass</h4><p className="text-sm text-muted-foreground">Thrives 80-95°F. Bermudagrass, Zoysiagrass, St. Augustinegrass. Peak growth: summer.</p></div>
                      <div><h4 className="font-bold text-sm">ET (Evapotranspiration)</h4><p className="text-sm text-muted-foreground">Combined water loss from soil evaporation and plant transpiration. Basis for smart irrigation scheduling.</p></div>
                      <div><h4 className="font-bold text-sm">T_base (Base Temperature)</h4><p className="text-sm text-muted-foreground">Minimum temperature below which growth is assumed zero. TurfPulse uses 50°F for both cool and warm-season GDD.</p></div>
                    </div>
                  )
                },
              ];

              const q = manualSearch.toLowerCase();
              const filtered = q
                ? manualSections.filter(s =>
                    s.title.toLowerCase().includes(q) ||
                    s.keywords.toLowerCase().includes(q)
                  )
                : manualSections;

              return (
                <div className="space-y-4">
                  {/* Search Bar */}
                  <div className="relative">
                    <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      data-testid="ops-manual-search"
                      placeholder="Search the Ops Manual — try 'refund', 'FIFRA', 'comp account', 'Stripe'..."
                      value={manualSearch}
                      onChange={(e) => setManualSearch(e.target.value)}
                      className="pl-12 h-12 text-base border-2 focus:border-primary"
                    />
                    {manualSearch && (
                      <button
                        onClick={() => setManualSearch('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {manualSearch && (
                    <p className="text-sm text-muted-foreground">
                      {filtered.length} section{filtered.length !== 1 ? 's' : ''} matching "{manualSearch}"
                    </p>
                  )}

                  {filtered.length > 0 ? (
                    <Accordion
                      type="multiple"
                      defaultValue={[]}
                      value={manualSearch ? filtered.map(s => s.id) : undefined}
                      className="space-y-2"
                    >
                      {filtered.map((section) => (
                        <AccordionItem
                          key={section.id}
                          value={section.id}
                          className="border rounded-lg px-4 data-[state=open]:bg-muted/30"
                          data-testid={`ops-section-${section.id}`}
                        >
                          <AccordionTrigger className="hover:no-underline text-base font-semibold py-4">
                            {section.title}
                          </AccordionTrigger>
                          <AccordionContent className="pb-4">
                            {section.content}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <MagnifyingGlass size={48} className="mx-auto mb-4 opacity-30" />
                      <p className="font-medium">No sections match "{manualSearch}"</p>
                      <p className="text-sm mt-1">Try different keywords or clear the search</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>

        {/* Add Partner Dialog */}
        <Dialog open={showAddPartner} onOpenChange={setShowAddPartner}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Partner Manually</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              {[
                { key: 'business_name', label: 'Business Name *', placeholder: 'e.g., ABC Lawn Care' },
                { key: 'contact_name', label: 'Contact Name', placeholder: 'e.g., John Smith' },
                { key: 'email', label: 'Email', placeholder: 'john@abclawncare.com' },
                { key: 'phone', label: 'Phone', placeholder: '(512) 555-1234' },
                { key: 'service_type', label: 'Service Type', placeholder: 'e.g., Lawn Maintenance, Pest Control, Irrigation' },
                { key: 'area_served', label: 'Area Served', placeholder: 'e.g., Round Rock, TX / Austin Metro' },
                { key: 'website', label: 'Website', placeholder: 'https://abclawncare.com' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-medium text-muted-foreground">{f.label}</label>
                  <input
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder={f.placeholder}
                    value={newPartner[f.key]}
                    onChange={(e) => setNewPartner(prev => ({ ...prev, [f.key]: e.target.value }))}
                    data-testid={`add-partner-${f.key}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <textarea
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] resize-y"
                  placeholder="Brief description of services..."
                  value={newPartner.description}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="add-partner-description"
                />
              </div>
              <Button className="w-full" onClick={handleAddPartner} data-testid="add-partner-submit">
                Add Partner (Auto-Approved)
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Partner Dialog */}
        <Dialog open={!!editingPartner} onOpenChange={(open) => !open && setEditingPartner(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-partner-dialog">
            <DialogHeader>
              <DialogTitle>Edit Partner</DialogTitle>
              <DialogDescription>Update contact info and service details.</DialogDescription>
            </DialogHeader>
            {editingPartner && (
              <div className="space-y-3 mt-2">
                {[
                  { key: 'business_name', label: 'Business Name *', placeholder: 'e.g., ABC Lawn Care' },
                  { key: 'contact_name', label: 'Contact Name', placeholder: 'e.g., John Smith' },
                  { key: 'email', label: 'Email', placeholder: 'john@abclawncare.com' },
                  { key: 'phone', label: 'Phone', placeholder: '(512) 555-1234' },
                  { key: 'service_type', label: 'Service Type', placeholder: 'e.g., Lawn Maintenance' },
                  { key: 'area_served', label: 'Area Served', placeholder: 'e.g., Austin Metro' },
                  { key: 'website', label: 'Website', placeholder: 'https://abclawncare.com' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-xs font-medium text-muted-foreground">{f.label}</label>
                    <input
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder={f.placeholder}
                      value={editingPartner[f.key] || ''}
                      onChange={(e) => setEditingPartner(prev => ({ ...prev, [f.key]: e.target.value }))}
                      data-testid={`edit-partner-${f.key}`}
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Description</label>
                  <textarea
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] resize-y"
                    placeholder="Brief description of services..."
                    value={editingPartner.description || ''}
                    onChange={(e) => setEditingPartner(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="edit-partner-description"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1" onClick={() => setEditingPartner(null)} disabled={partnerSaving}>
                    Cancel
                  </Button>
                  <Button className="flex-1" onClick={handleEditPartner} disabled={partnerSaving} data-testid="edit-partner-submit">
                    {partnerSaving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
